import { renderIcon } from "./icons";
import { renderMessageTemplate } from "../../public/shared/manager/inboxTemplates.js";
import { apiUrl } from "./api";
import { EPIC_GOOGLE_WEB_SIGN_IN_PATH, MOBILE_GUEST_ENABLED, resolveGoogleSignInSurface, resolveMobileApiPrefix } from "./environment";
import { profileImageUrl, publicProfileUrl } from "./accountProfile";
import type { MobileAppState } from "./state";
import type {
  AccountSummary,
  InboxConversation,
  InboxHistoryReview,
  InboxListItem,
  InboxMessage,
  MobileTab,
  MobileUiAction,
  MobileUiActionName,
  MobileUiCompanionField,
  MobileUiCompanionFieldKey,
  MobileUiComponent,
  MobileUiComponentKind,
  MobileUiSummaryItem,
  SafeError,
  WctekChatMessage
} from "./types";

export function escapeHtml(value: unknown) {
  return String(value ?? "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;"
  })[character] || character);
}

function validDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatRowTime(value: string | null | undefined, fallback: string) {
  const date = validDate(value);
  if (!date) return fallback;
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
  const elapsedDays = Math.floor((now.getTime() - date.getTime()) / 86400000);
  if (elapsedDays >= 0 && elapsedDays < 7) return new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(date);
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date);
}

function formatMessageTime(value: string | null | undefined, fallback: string) {
  const date = validDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
}

function dayKey(value?: string | null) {
  const date = validDate(value);
  return date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` : "";
}

function formatDay(value?: string | null) {
  const date = validDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric", year: "numeric" }).format(date);
}

function readableToken(value: unknown, fallback: string) {
  const normalized = String(value || "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : fallback;
}

function attachmentLabel(attachment: NonNullable<InboxMessage["attachments"]>[number]) {
  const kind = readableToken(attachment.mediaKind, "Attachment");
  if (["Share", "Shared post"].includes(kind)) return "Shared content";
  if (["Reel", "Shared reel"].includes(kind)) return "Shared reel";
  if (["Audio", "Voice"].includes(kind)) return "Voice message";
  return `${kind} attachment`;
}

function attachmentPreviewUrl(path: unknown) {
  const value = String(path || "");
  if (!/^\/inbox\/conversations\/conv_[a-f0-9]{16,64}\/messages\/(?:message|msg)_[A-Za-z0-9_-]{8,100}\/attachments\/safe_attachment_[a-f0-9]{32}\/preview$/.test(value)) return null;
  return apiUrl(value, resolveMobileApiPrefix());
}

function templateAttachmentReferences(message: InboxMessage) {
  return new Set((message.structuredContent?.elements || [])
    .map((element) => element?.imageAttachmentRef)
    .filter((reference) => /^safe_attachment_[a-f0-9]{32}$/.test(String(reference || ""))));
}

function renderAttachment(attachment: NonNullable<InboxMessage["attachments"]>[number]) {
  const label = attachmentLabel(attachment);
  const source = attachmentPreviewUrl(attachment.preview?.path);
  if (source) {
    const kind = String(attachment.mediaKind || "unknown").toLowerCase();
    const media = kind === "video"
      ? `<video src="${escapeHtml(source)}" controls playsinline preload="metadata" aria-label="${escapeHtml(label)}" data-mobile-inbox-media-preview></video>`
      : ["audio", "voice"].includes(kind)
        ? `<audio src="${escapeHtml(source)}" controls preload="metadata" aria-label="${escapeHtml(label)}" data-mobile-inbox-media-preview></audio>`
        : `<img src="${escapeHtml(source)}" alt="${escapeHtml(label)}" loading="lazy" decoding="async" referrerpolicy="no-referrer" data-mobile-inbox-media-preview>`;
    return `<li class="message-attachment-preview inbox-media-preview">${media}<strong>${escapeHtml(label)}</strong><small class="message-attachment-preview__fallback">Preview unavailable</small></li>`;
  }
  const status = attachment.accessStatus === "locked" ? "Locked media"
    : attachment.accessStatus === "unavailable" ? "Media unavailable"
    : attachment.captureStatus === "capture_queued" ? "Media save pending"
    : attachment.captureStatus === "capture_failed" ? "Media save failed"
    : attachment.captureStatus === "captured" ? "Saved media; preview unavailable"
    : "Attachment preview unavailable";
  return `<li>${renderIcon("image")}<div><strong>${escapeHtml(label)}</strong><small>${escapeHtml(status)}</small></div></li>`;
}

function accountLabel(account: AccountSummary | null, fallback: string) {
  return account?.displayName || account?.username || fallback;
}

function initials(value: string | null | undefined, fallback: string) {
  return String(value || fallback)
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || "")
    .join("")
    .toUpperCase()
    .slice(0, 2) || fallback.slice(0, 1).toUpperCase();
}

function contactName(state: MobileAppState, conversation: InboxConversation) {
  const contact = conversation.contact;
  const name = contact?.displayName?.trim();
  const placeholder = !name || /^(?:unknown(?: imported)? instagram contact(?:\s*[\u00b7:]\s*\w+)?|instagram contact|unknown participant|contact)$/i.test(name);
  const username = conversation.history?.origin === "imported_history" ? null : contact?.providerUsername?.trim().replace(/^@+/, "");
  return placeholder && username ? `@${username}` : name || state.ui.app.copy.contactFallback;
}

function configuredCompanionName(state: MobileAppState) {
  return String(state.companion.saved?.aiName || "").trim() || "My AI companion";
}

function safeImageUrl(value: unknown) {
  const normalized = String(value || "").trim();
  if (!normalized || normalized.length > 2048) return null;
  try {
    const parsed = new URL(normalized, typeof location === "undefined" ? "https://ctek123.pro" : location.origin);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return parsed.href;
  } catch (_) {
    return null;
  }
}

function renderConversationAvatar(state: MobileAppState, conversation: InboxConversation, name: string) {
  const profileImageUrl = safeImageUrl(conversation.contact?.profileImageUrl);
  return profileImageUrl
    ? `<span class="avatar avatar--photo" aria-hidden="true"><img src="${escapeHtml(profileImageUrl)}" alt="" loading="lazy" referrerpolicy="no-referrer" /></span>`
    : `<span class="avatar" aria-hidden="true">${escapeHtml(initials(name, state.ui.app.brandLabel))}</span>`;
}

function isHistoryReview(item: InboxListItem): item is InboxHistoryReview {
  return item.recordKind === "history_review";
}

function actionAttributes(action: MobileUiAction | null | undefined, attributes: Record<string, string> = {}) {
  if (!action) return "";
  const parts = [`data-mobile-action="${escapeHtml(action.name)}"`];
  if (action.name === "navigate" && action.tab) parts.push(`data-tab="${escapeHtml(action.tab)}"`);
  if (action.name === "open-profile-view" && action.profileView) parts.push(`data-profile-view="${escapeHtml(action.profileView)}"`);
  for (const [key, value] of Object.entries(attributes)) {
    parts.push(`data-${escapeHtml(key)}="${escapeHtml(value)}"`);
  }
  return parts.join(" ");
}

function actionFor(name: MobileUiActionName, label: string, icon: MobileUiAction["icon"] = "refresh-cw"): MobileUiAction {
  return { name, label, icon, tab: null };
}

function iconButton(action: MobileUiAction, className = "icon-button", attributes: Record<string, string> = {}) {
  return `<button class="${className}" type="button" ${actionAttributes(action, attributes)} aria-label="${escapeHtml(action.label)}" title="${escapeHtml(action.label)}">
    ${renderIcon(action.icon || "arrow-right")}
  </button>`;
}

function textButton(action: MobileUiAction, className = "mobile-button", attributes: Record<string, string> = {}) {
  return `<button class="${className}" type="button" ${actionAttributes(action, attributes)}>
    ${action.icon ? renderIcon(action.icon) : ""}
    <span>${escapeHtml(action.label)}</span>
  </button>`;
}

function errorState(state: MobileAppState, error: SafeError | null, retryAction: MobileUiActionName, retryLabel?: string) {
  const copy = state.ui.app.copy;
  const retry = error?.retryable !== false
    ? textButton(actionFor(retryAction, retryLabel || copy.retryLabel), "mobile-button mobile-button--secondary")
    : "";
  return `<section class="state-panel state-panel--error" role="alert">
    <span class="state-panel__icon">${renderIcon("circle-alert")}</span>
    <div>
      <h2>${escapeHtml(copy.errorTitle)}</h2>
      <p>${escapeHtml(error?.message || copy.errorTitle)}</p>
    </div>
    ${retry}
  </section>`;
}

function loadingRows(label: string, count = 4) {
  return `<div class="skeleton-list" role="status" aria-label="${escapeHtml(label)}">
    ${Array.from({ length: count }, () => `<div class="skeleton-row" aria-hidden="true">
      <span class="skeleton skeleton--avatar"></span>
      <span class="skeleton-row__body"><span class="skeleton skeleton--line"></span><span class="skeleton skeleton--line skeleton--short"></span></span>
    </div>`).join("")}
  </div>`;
}

interface RenderContext {
  state: MobileAppState;
  tab: MobileTab;
}

type ComponentRenderer = (component: MobileUiComponent, context: RenderContext) => string;

const renderAppHeader: ComponentRenderer = (_component, { state }) => {
  const active = state.activeAccount;
  const copy = state.ui.app.copy;
  const accountControl = state.sessionState === "ready"
    ? `<div class="account-chip" title="${escapeHtml(accountLabel(active, copy.workspaceLabel))}" aria-label="${escapeHtml(copy.workspaceLabel)}: ${escapeHtml(accountLabel(active, copy.workspaceLabel))}">
        <span class="account-chip__avatar" aria-hidden="true">${escapeHtml(initials(accountLabel(active, copy.workspaceLabel), state.ui.app.brandLabel))}</span>
        <span class="account-chip__copy"><small>${escapeHtml(copy.workspaceLabel)}</small><strong>${escapeHtml(accountLabel(active, copy.workspaceLabel))}</strong></span>
      </div>`
    : "";
  const brandAction: MobileUiAction = { name: "navigate", label: state.ui.tabs.find((tab) => tab.key === "home")?.label || "Home", icon: "home", tab: "home" };
  return `<header class="app-header">
    <button class="brand-lockup" type="button" ${actionAttributes(brandAction)} aria-label="${escapeHtml(state.ui.app.brandLabel)}">
      <span class="brand-mark" aria-hidden="true"><img src="./icon-192.png" srcset="./icon-192.png 192w, ./icon-512.png 512w" alt="" /></span>
      <span class="brand-copy"><strong>${escapeHtml(state.ui.app.brandLabel)}</strong><small>${escapeHtml(state.ui.app.productLabel)}</small></span>
    </button>
    <div class="app-header__account">${accountControl}</div>
  </header>`;
};

const renderBottomNavigation: ComponentRenderer = (_component, { state }) => `<nav class="bottom-navigation" aria-label="Primary">
  ${state.ui.tabs.map((tab) => {
    const action: MobileUiAction = { name: "navigate", label: tab.label, icon: tab.icon, tab: tab.key };
    return `<button class="nav-item" type="button" ${actionAttributes(action)} aria-label="${escapeHtml(tab.label)}" ${state.activeTab === tab.key ? 'aria-current="page"' : ""}>
      ${renderIcon(tab.icon)}
      <span>${escapeHtml(tab.label)}</span>
    </button>`;
  }).join("")}
</nav>`;

const renderConnectionStatus: ComponentRenderer = (component, { state }) => {
  if (state.online) return "";
  return `<aside class="connection-banner connection-banner--${escapeHtml(component.tone || "warning")}" role="status">
    ${renderIcon(component.icon || "wifi-off")}
    <div><strong>${escapeHtml(component.title)}</strong><p>${escapeHtml(component.body)}</p></div>
  </aside>`;
};

const renderAccountOverview: ComponentRenderer = (component, { state }) => {
  const active = accountLabel(state.activeAccount, state.ui.app.copy.workspaceLabel);
  const status = state.online ? component.labels?.online : component.labels?.offline;
  return `<section class="account-overview" aria-labelledby="account-overview-title">
    <div>
      ${component.eyebrow ? `<p class="eyebrow">${escapeHtml(component.eyebrow)}</p>` : ""}
      <h1 id="account-overview-title">${escapeHtml(active)}</h1>
      ${component.body ? `<p class="account-overview__body">${escapeHtml(component.body)}</p>` : ""}
    </div>
    <span class="status-pill status-pill--${state.online ? "success" : "warning"}"><span aria-hidden="true"></span>${escapeHtml(status)}</span>
  </section>`;
};

function renderHistoryReviewRow(state: MobileAppState, review: InboxHistoryReview, labels: Record<string, string>) {
  const title = review.title || labels.historyReview || "Imported history needs review";
  const body = labels.historyReviewBody || "This imported history could not be linked safely to a contact conversation.";
  const details = [
    `${Math.max(0, Number(review.threadCount || 0))} threads`,
    `${Math.max(0, Number(review.messageCount || 0))} messages`
  ];
  return `<article class="conversation-row conversation-row--review" role="note" aria-label="${escapeHtml(title)}">
    <span class="avatar avatar--warning" aria-hidden="true">${renderIcon("circle-alert")}</span>
    <span class="conversation-row__content">
      <span class="conversation-row__heading"><strong>${escapeHtml(title)}</strong><time>${escapeHtml(formatRowTime(review.lastObservedAt, state.ui.app.copy.timeUnavailable))}</time></span>
      <span class="conversation-row__preview">${escapeHtml(body)}</span>
      <span class="conversation-row__meta"><span>${renderIcon("image")}${escapeHtml(labels.instagramChannel || "Instagram")}</span><span>${escapeHtml(details.join(" · "))}</span></span>
    </span>
    <span class="row-status" aria-hidden="true">${renderIcon("shield-check")}</span>
  </article>`;
}

function renderConversationRow(state: MobileAppState, item: InboxListItem, compact = false, labels: Record<string, string> = {}) {
  if (isHistoryReview(item)) return compact ? "" : renderHistoryReviewRow(state, item, labels);
  const conversation = item;
  const copy = state.ui.app.copy;
  const name = contactName(state, conversation);
  const latest = conversation.latestMessage?.deleted
    ? labels.deletedPreview || "Message removed"
    : conversation.latestMessage?.preview || copy.noPreview;
  const occurredAt = conversation.latestInboundAt || conversation.lastMessageAt || conversation.latestMessage?.occurredAt;
  const channel = readableToken(conversation.channel, copy.channelFallback);
  const secondary = conversation.contact?.secondaryLabel || conversation.assignee?.label || copy.unassignedLabel;
  const selected = state.inbox.selectedConversation?.conversationRef === conversation.conversationRef;
  const openAction = actionFor("open-conversation", name, "message-circle");
  const history = conversation.history?.origin === "imported_history"
    ? conversation.history?.completeness === "partial"
      ? labels.partialHistory || "Partial history"
      : labels.importedHistory || "Imported"
    : null;
  const companionState = conversation.olivia?.state
    ? `${configuredCompanionName(state)}: ${readableToken(conversation.olivia.state, labels.companionState || "Unavailable")}`
    : null;
  const contactHistory = Number(conversation.contactHistory?.conversationCount || 0) > 1
    ? `${Number(conversation.contactHistory?.conversationCount)} ${labels.contactHistory || "conversations"}`
    : null;
  const rowLabels = (conversation.labels || []).slice(0, 2);
  const hasAttachment = Number(conversation.latestMessage?.attachmentCount || 0) > 0;
  return `<button class="conversation-row${compact ? " conversation-row--compact" : ""}${selected ? " is-selected" : ""}" type="button" ${actionAttributes(openAction, { "conversation-ref": conversation.conversationRef })} ${selected ? 'aria-current="true"' : ""}>
    ${renderConversationAvatar(state, conversation, name)}
    <span class="conversation-row__content">
      <span class="conversation-row__heading"><strong>${escapeHtml(name)}</strong><time>${escapeHtml(formatRowTime(occurredAt, copy.timeUnavailable))}</time></span>
      <span class="conversation-row__preview${conversation.latestMessage?.deleted ? " is-deleted" : ""}">${escapeHtml(latest)}</span>
      ${compact ? "" : `<span class="conversation-row__meta">
        <span>${renderIcon("message-circle")}${escapeHtml(channel)}</span>
        ${history ? `<span class="conversation-signal">${escapeHtml(history)}</span>` : ""}
        <span>${escapeHtml(secondary)}</span>
        ${contactHistory ? `<span>${renderIcon("users")}${escapeHtml(contactHistory)}</span>` : ""}
        ${companionState ? `<span>${renderIcon("sparkles")}${escapeHtml(companionState)}</span>` : ""}
        ${hasAttachment ? `<span aria-label="${escapeHtml(labels.attachment || "Has attachment")}">${renderIcon("paperclip")}</span>` : ""}
      </span>`}
      ${compact || !rowLabels.length ? "" : `<span class="conversation-row__labels">${rowLabels.map((label) => `<span>${renderIcon("tag")}${escapeHtml(label.name)}</span>`).join("")}</span>`}
    </span>
    ${conversation.unreadCount ? `<span class="unread-badge" aria-label="${Number(conversation.unreadCount)} ${escapeHtml(copy.unreadLabel)}">${Math.min(99, Number(conversation.unreadCount))}</span>` : `<span class="row-chevron">${renderIcon("chevron-right")}</span>`}
  </button>`;
}

const renderInboxPreview: ComponentRenderer = (component, { state }) => {
  const conversations = state.inbox.conversations.filter((item) => !isHistoryReview(item)).slice(0, component.maxItems || 3);
  const action = component.action ? textButton(component.action, "text-link") : "";
  let content = "";
  if (state.inbox.listState === "loading" || state.inbox.listState === "idle") content = loadingRows(component.labels?.loading || state.ui.app.loadingMessage, 3);
  else if (state.inbox.listState === "error" || state.inbox.listState === "forbidden") content = errorState(state, state.inbox.error, state.inbox.listState === "forbidden" ? "retry-session" : "retry-inbox");
  else if (conversations.length) content = `<div class="conversation-list conversation-list--preview">${conversations.map((conversation) => renderConversationRow(state, conversation, true)).join("")}</div>`;
  else content = `<div class="inline-empty"><span>${renderIcon("inbox")}</span><p>${escapeHtml(component.labels?.empty)}</p></div>`;
  return `<section class="content-section" aria-labelledby="${escapeHtml(component.id)}-title">
    <div class="section-heading">
      <div>${component.eyebrow ? `<p class="eyebrow">${escapeHtml(component.eyebrow)}</p>` : ""}<h2 id="${escapeHtml(component.id)}-title">${escapeHtml(component.title)}</h2>${component.body ? `<p>${escapeHtml(component.body)}</p>` : ""}</div>
      ${action}
    </div>
    ${content}
  </section>`;
};

function renderSummaryItem(item: MobileUiSummaryItem, state: MobileAppState) {
  const tag = item.action ? "button" : "article";
  const attributes = item.action ? `type="button" ${actionAttributes(item.action)} aria-label="${escapeHtml(item.action.label)}"` : "";
  const savedCompanionName = String(state.companion.saved?.aiName || "").trim();
  const title = item.titleSource === "active-companion-name" && savedCompanionName
    ? savedCompanionName
    : item.title;
  return `<${tag} class="tool-card tool-card--${escapeHtml(item.tone)}" ${attributes}>
    <span class="tool-card__icon">${renderIcon(item.icon)}</span>
    <span class="tool-card__copy"><span class="tool-card__status">${escapeHtml(item.status)}</span><strong>${escapeHtml(title)}</strong><span>${escapeHtml(item.body)}</span></span>
    ${item.action ? `<span class="tool-card__arrow">${renderIcon("arrow-right")}</span>` : ""}
  </${tag}>`;
}

const renderSummaryGrid: ComponentRenderer = (component, { state }) => `<section class="content-section content-section--${escapeHtml(component.id)}" aria-labelledby="${escapeHtml(component.id)}-title">
  <div class="section-heading"><div>${component.eyebrow ? `<p class="eyebrow">${escapeHtml(component.eyebrow)}</p>` : ""}<h2 id="${escapeHtml(component.id)}-title">${escapeHtml(component.title)}</h2></div></div>
  <div class="tool-grid">${(component.items || []).map((item) => renderSummaryItem(item, state)).join("")}</div>
</section>`;

const renderScreenHeading: ComponentRenderer = (component) => `<section class="screen-heading" aria-labelledby="${escapeHtml(component.id)}-title">
  <div>${component.eyebrow ? `<p class="eyebrow">${escapeHtml(component.eyebrow)}</p>` : ""}<h1 id="${escapeHtml(component.id)}-title">${escapeHtml(component.title)}</h1>${component.body ? `<p>${escapeHtml(component.body)}</p>` : ""}</div>
  ${component.action ? iconButton(component.action) : ""}
</section>`;

function inboxActiveFilterCount(state: MobileAppState) {
  const filters = state.inbox.filters;
  return Number(Boolean(filters.search))
    + Number(filters.channel !== "all")
    + Number(filters.status !== "open")
    + Number(filters.sort !== "latest_inbound")
    + Number(filters.folder !== "all")
    + filters.labelIds.length;
}

function option(value: string, label: string, selected: string) {
  return `<option value="${escapeHtml(value)}"${value === selected ? " selected" : ""}>${escapeHtml(label)}</option>`;
}

function renderInboxFilterSheet(component: MobileUiComponent, state: MobileAppState) {
  if (!state.inbox.filtersOpen) return "";
  const labels = component.labels || {};
  const draft = state.inbox.filterDraft;
  const close = actionFor("close-inbox-filters", labels.closeFilters || "Close filters", "x");
  const reset = actionFor("reset-inbox-filters", labels.resetFilters || "Clear filters", "x");
  const retryMetadata = actionFor("retry-inbox-metadata", state.ui.app.copy.retryLabel, "refresh-cw");
  const folders = state.inbox.folders.length
    ? state.inbox.folders
    : [{ folderId: "all" as const, name: labels.allStatuses || "All chats", count: 0 }];
  const channelOptions = [
    ["all", labels.allChannels || "All channels"],
    ["instagram", labels.instagramChannel || "Instagram"],
    ["tiktok", labels.tiktokChannel || "TikTok"],
    ["x", labels.xChannel || "X"],
    ["sms", labels.smsChannel || "SMS"],
    ["rcs", labels.rcsChannel || "RCS"],
    ["imessage", labels.imessageChannel || "iMessage"],
    ["olivia", labels.companionChannel || "EPIC / Companion"]
  ];
  const metadata = state.inbox.metadataState === "loading"
    ? `<p class="inbox-filter-state" role="status">${escapeHtml(labels.loading || state.ui.app.loadingMessage)}</p>`
    : state.inbox.metadataState === "error" || state.inbox.metadataState === "forbidden"
      ? `<div class="inbox-filter-state" role="alert"><p>${escapeHtml(state.inbox.metadataError?.message || labels.metadataUnavailable || "Folders and labels are unavailable.")}</p>${textButton(retryMetadata, "mobile-button mobile-button--secondary")}</div>`
      : "";
  return `<div class="inbox-filter-layer">
    <button class="inbox-filter-backdrop" type="button" ${actionAttributes(close)} aria-label="${escapeHtml(close.label)}" tabindex="-1"></button>
    <aside class="inbox-filter-sheet" role="dialog" aria-modal="true" aria-labelledby="inbox-filter-title">
      <header class="inbox-filter-sheet__header"><div><p class="eyebrow">${escapeHtml(component.eyebrow || "Inbox")}</p><h2 id="inbox-filter-title">${escapeHtml(labels.filters || "Filter Inbox")}</h2></div>${iconButton(close, "icon-button", { "inbox-filter-close": "true" })}</header>
      <form class="inbox-filter-form" data-mobile-inbox-filter-form>
        <label class="settings-field settings-field--wide"><span>${escapeHtml(labels.search || "Search")}</span><span class="inbox-search-control">${renderIcon("search")}<input type="search" value="${escapeHtml(draft.search)}" placeholder="${escapeHtml(labels.searchPlaceholder || "Name, handle, or message")}" autocomplete="off" data-mobile-inbox-filter="search" /></span></label>
        <div class="inbox-filter-grid">
          <label class="settings-field"><span>${escapeHtml(labels.channel || "Channel")}</span><select data-mobile-inbox-filter="channel">${channelOptions.map(([value, label]) => option(value, label, draft.channel)).join("")}</select></label>
          <label class="settings-field"><span>${escapeHtml(labels.status || "Status")}</span><select data-mobile-inbox-filter="status">${option("open", labels.open || "Open", draft.status)}${option("closed", labels.closed || "Closed", draft.status)}${option("all", labels.allStatuses || "All statuses", draft.status)}</select></label>
          <label class="settings-field settings-field--wide"><span>${escapeHtml(labels.sort || "Sort")}</span><select data-mobile-inbox-filter="sort">${option("latest_inbound", labels.latestInbound || "Latest inbound", draft.sort)}${option("latest_message", labels.latestMessage || "Latest message", draft.sort)}</select></label>
        </div>
        ${metadata}
        <fieldset class="inbox-filter-group"><legend>${escapeHtml(labels.folders || "Folders")}</legend><div class="inbox-choice-list">${folders.map((folder) => `<label class="inbox-choice"><input type="radio" name="inbox-folder" value="${escapeHtml(folder.folderId)}" data-mobile-inbox-filter="folder"${folder.folderId === draft.folder ? " checked" : ""} /><span>${escapeHtml(folder.name)}</span><strong>${Math.max(0, Number(folder.count || 0))}</strong></label>`).join("")}</div></fieldset>
        <fieldset class="inbox-filter-group"><legend>${escapeHtml(labels.labels || "Labels")}</legend>${state.inbox.labels.length ? `<div class="inbox-choice-list">${state.inbox.labels.map((label) => `<label class="inbox-choice"><input type="checkbox" value="${escapeHtml(label.labelId)}" data-mobile-inbox-label${draft.labelIds.includes(label.labelId) ? " checked" : ""} /><span>${renderIcon("tag")}${escapeHtml(label.name)}</span></label>`).join("")}</div>` : `<p class="inbox-filter-state">${escapeHtml(labels.noLabels || "No labels are available.")}</p>`}</fieldset>
        <div class="inbox-filter-actions"><button class="mobile-button" type="submit"${state.online ? "" : " disabled"}>${renderIcon("list-filter")}<span>${escapeHtml(state.online ? labels.applyFilters || "Apply filters" : labels.offline || "Reconnect to apply")}</span></button>${textButton(reset, "mobile-button mobile-button--secondary")}</div>
      </form>
    </aside>
  </div>`;
}

const renderConversationList: ComponentRenderer = (component, { state }) => {
  const inbox = state.inbox;
  const labels = component.labels || {};
  let content = "";
  if (inbox.listState === "loading" || inbox.listState === "idle") content = loadingRows(component.labels?.loading || state.ui.app.loadingMessage, 6);
  else if (inbox.listState === "forbidden") content = errorState(state, inbox.error, "retry-session");
  else if (inbox.listState === "error") content = errorState(state, inbox.error, "retry-inbox");
  else if (inbox.listState === "empty") content = `<div class="empty-state"><span>${renderIcon("inbox")}</span><h2>${escapeHtml(component.labels?.empty)}</h2></div>`;
  else content = `<div class="conversation-list">${inbox.conversations.map((conversation) => renderConversationRow(state, conversation, false, labels)).join("")}</div>`;
  const moreAction = actionFor("load-more-conversations", component.labels?.loadMore || "", "arrow-right");
  const filtersAction = actionFor("toggle-inbox-filters", labels.filters || "Filter Inbox", "list-filter");
  const activeFilters = inboxActiveFilterCount(state);
  return `<section class="conversation-list-panel" aria-label="${escapeHtml(component.labels?.ariaLabel)}">
    <div class="inbox-list-toolbar">
      <span aria-live="polite">${activeFilters ? `${activeFilters} ${escapeHtml(labels.activeFilters || "active filters")}` : escapeHtml(labels.defaultView || "All open contacts")}</span>
      <div class="inbox-list-actions"><button class="icon-button" type="button" data-mobile-sms-open hidden aria-label="Phone SMS" title="Phone SMS">${renderIcon("message-circle")}</button>
      <button class="mobile-button mobile-button--secondary inbox-filter-button" type="button" ${actionAttributes(filtersAction)} aria-expanded="${inbox.filtersOpen ? "true" : "false"}" aria-controls="inbox-filter-title">${renderIcon("list-filter")}<span>${escapeHtml(filtersAction.label)}</span>${activeFilters ? `<strong>${activeFilters}</strong>` : ""}</button>
      </div>
    </div>
    ${content}
    ${inbox.page?.hasMore ? textButton(moreAction, "mobile-button mobile-button--secondary mobile-button--wide") : ""}
    ${renderInboxFilterSheet(component, state)}
  </section>`;
};

function renderMessage(state: MobileAppState, message: InboxMessage) {
  const copy = state.ui.app.copy;
  const deleted = message.lifecycle?.deleted === true;
  if (deleted && message.channel === "onlyfans") {
    message = { ...message, text: null, safeSummary: null, attachments: [], template: null, lifecycle: { ...message.lifecycle, reactions: [] } };
  }
  const direction = message.direction === "outbound" ? "outbound" : message.direction === "inbound" ? "inbound" : "system";
  const generatedAttachmentSummary = (message.attachments?.length || 0) > 0
    && /^Instagram (?:unknown|attachment|image|video|audio|voice|gif|sticker|share|shared media|reel|story|file|message containing \d+ attachments) received\.$/i.test(message.safeSummary || "");
  const body = message.text || (!deleted && (message.template || message.structuredContent || generatedAttachmentSummary) ? ""
    : message.safeSummary || (message.attachments?.length ? "" : copy.contentUnavailable));
  const author = message.author?.label || (direction === "system" ? copy.systemAuthor : direction === "outbound" ? state.ui.app.brandLabel : copy.contactFallback);
  const delivery = message.lifecycle?.delivery?.status || message.delivery?.status;
  const templateReferences = templateAttachmentReferences(message);
  const attachments = deleted ? [] : (message.attachments || [])
    .filter((attachment) => !templateReferences.has(attachment.safeAttachmentRef || ""))
    .slice(0, 20);
  const reactions = (message.lifecycle?.reactions || []).filter((reaction) => reaction.active === true).slice(0, 20);
  const imported = message.origin === "imported_history" || message.author?.imported === true;
  return `<li class="message-row message-row--${escapeHtml(direction)}" data-message-ref="${escapeHtml(message.messageRef)}">
    <div class="message-bubble">
      <span class="message-bubble__author">${escapeHtml(author)}${imported ? " · Imported history" : ""}</span>
      ${message.replyContext ? `<p class="message-reply-context">${escapeHtml(message.replyContext.label)}${message.replyContext.available ? "" : " · Original preview unavailable"}</p>` : ""}
      ${body ? `<p>${deleted ? `<del>${escapeHtml(body)}</del> <span>Deleted</span>` : escapeHtml(body)}</p>` : ""}
      ${renderMessageTemplate(message, { resolvePreviewUrl: attachmentPreviewUrl })}
      ${attachments.length ? `<ul class="message-attachments">${attachments.map(renderAttachment).join("")}</ul>` : ""}
      ${reactions.length ? `<div class="message-reactions" aria-label="Reactions">${reactions.map((reaction) => `<span>${escapeHtml(reaction.value)}</span>`).join("")}</div>` : ""}
      <span class="message-bubble__meta">${message.lifecycle?.edited && !deleted ? "<span>Edited</span>" : ""}<time>${escapeHtml(formatMessageTime(message.occurredAt, copy.timeUnavailable))}</time>${delivery ? `<span>${escapeHtml(readableToken(delivery, ""))}</span>` : ""}</span>
    </div>
  </li>`;
}

function renderTimeline(state: MobileAppState) {
  if (!state.inbox.messages.length) return "";
  let currentDay = "";
  return `<ol class="message-list">${state.inbox.messages.map((message) => {
    const nextDay = dayKey(message.occurredAt);
    const divider = nextDay && nextDay !== currentDay
      ? `<li class="date-divider"><span>${escapeHtml(formatDay(message.occurredAt))}</span></li>`
      : "";
    currentDay = nextDay || currentDay;
    return `${divider}${renderMessage(state, message)}`;
  }).join("")}</ol>`;
}

function renderInboxComposer(component: MobileUiComponent, state: MobileAppState) {
  const inbox = state.inbox;
  if (inbox.selectedConversation?.phoneSms?.registeredPhoneOnly) {
    return `<section class="reply-boundary"><span>${renderIcon("message-circle")}</span><div><strong>Phone SMS</strong>
      <button type="button" class="mobile-button" data-mobile-sms-reply hidden>${renderIcon("send-horizontal")}<span>Reply by SMS</span></button></div></section>`;
  }
  const labels = component.labels || {};
  const capabilities = inbox.selectedConversation?.capabilities;
  const noteEnabled = capabilities?.internalNote?.enabled === true;
  const replyEnabled = capabilities?.reply?.enabled === true;
  if (!noteEnabled && !replyEnabled) {
    return `<section class="reply-boundary" aria-label="${escapeHtml(labels.replyTitle)}">
      <span>${renderIcon("shield-check")}</span>
      <div><strong>${escapeHtml(labels.replyTitle)}</strong><p>${escapeHtml(labels.replyBody)}</p></div>
    </section>`;
  }

  const mode = inbox.composerMode === "note" && noteEnabled ? "note" : replyEnabled ? "reply" : "note";
  const capability = mode === "note" ? capabilities?.internalNote : capabilities?.reply;
  const maximum = mode === "note" ? 4000 : 1000;
  const saving = inbox.composerState === "loading";
  const disabled = saving || !state.online || capability?.enabled !== true || !inbox.composerText.trim();
  const status = inbox.composerError?.message
    || inbox.composerStatus
    || (!state.online ? labels.composerOffline || "Reconnect before saving this draft." : "");
  const submitLabel = mode === "note" ? labels.saveNote || "Save internal note" : labels.queueReply || "Queue reply for review";

  return `<form class="inbox-composer" data-mobile-inbox-composer-form aria-label="${escapeHtml(labels.composerLabel || "Inbox composer")}">
    <div class="inbox-composer__modes" role="group" aria-label="${escapeHtml(labels.composerMode || "Composer mode")}">
      <button type="button" data-mobile-inbox-composer-mode="reply" aria-pressed="${mode === "reply" ? "true" : "false"}" ${replyEnabled && !saving ? "" : "disabled"}>${escapeHtml(labels.replyMode || "Reply")}</button>
      <button type="button" data-mobile-inbox-composer-mode="note" aria-pressed="${mode === "note" ? "true" : "false"}" ${noteEnabled && !saving ? "" : "disabled"}>${escapeHtml(labels.noteMode || "Note")}</button>
    </div>
    <label class="sr-only" for="inbox-composer">${escapeHtml(mode === "note" ? labels.notePlaceholder || "Write an internal note" : labels.replyPlaceholder || "Write a reply")}</label>
    <textarea id="inbox-composer" data-mobile-inbox-composer maxlength="${maximum}" rows="3" placeholder="${escapeHtml(mode === "note" ? labels.notePlaceholder || "Write an internal note" : labels.replyPlaceholder || "Write a reply")}" ${saving ? "disabled" : ""}>${escapeHtml(inbox.composerText)}</textarea>
    <div class="inbox-composer__footer">
      <p class="inbox-composer__status${inbox.composerError || !state.online ? " is-error" : ""}" ${inbox.composerError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(status)}</p>
      <span>${inbox.composerText.length}/${maximum}</span>
      <button class="mobile-button mobile-button--primary" type="submit" ${disabled ? "disabled" : ""}>${renderIcon(mode === "note" ? "file-text" : "send-horizontal")}<span>${escapeHtml(saving ? labels.saving || "Saving..." : submitLabel)}</span></button>
    </div>
    ${mode === "reply" ? `<p class="inbox-composer__boundary">${escapeHtml(labels.replyReviewBoundary || "Replies are saved for human review and are not sent automatically.")}</p>` : `<p class="inbox-composer__boundary">${escapeHtml(labels.noteBoundary || "Notes stay inside EPIC and are never sent to the contact.")}</p>`}
  </form>`;
}

function renderInboxDraftReview(component: MobileUiComponent, state: MobileAppState) {
  const inbox = state.inbox;
  const conversation = inbox.selectedConversation;
  const draft = conversation?.reviewDraft;
  if (!conversation || !draft) return "";

  const labels = component.labels || {};
  const capabilities = conversation.capabilities;
  const busy = inbox.draftReviewState === "loading";
  const statusMessage = inbox.draftReviewError?.message || inbox.draftReviewStatus || "";
  const statusClass = inbox.draftReviewError || !state.online ? " is-error" : "";
  const statusLabels: Record<string, string> = {
    review_required: labels.draftNeedsReview || "Needs review",
    approved: labels.draftApproved || "Approved",
    approved_accepted: labels.draftAccepted || "Accepted for delivery",
    approved_sent: labels.draftSent || "Sent",
    approved_delivered: labels.draftDelivered || "Delivered",
    approved_failed: labels.draftFailed || "Delivery failed",
    rejected: labels.draftRejected || "Rejected",
    unavailable: labels.draftUnavailable || "Unavailable"
  };
  const stateLabel = statusLabels[draft.status] || labels.draftUnavailable || "Unavailable";
  const header = `<header class="inbox-draft-review__header">
    <span class="inbox-draft-review__icon">${renderIcon("shield-check")}</span>
    <div><p class="eyebrow">${escapeHtml(labels.draftEyebrow || "SMS review")}</p><h2>${escapeHtml(labels.draftReviewTitle || "Review AI draft")}</h2></div>
    <span class="status-pill status-pill--neutral">${escapeHtml(stateLabel)}</span>
  </header>`;

  if (draft.status === "review_required") {
    const canEdit = capabilities?.draftEdit?.enabled === true;
    const canApprove = capabilities?.draftApprove?.enabled === true;
    const canReject = capabilities?.draftReject?.enabled === true;
    const approveDisabled = busy || !state.online || !canApprove || !inbox.draftReviewText.trim();
    const rejectDisabled = busy || !state.online || !canReject;
    const unavailable = !canApprove && !canReject
      ? labels.draftReviewUnavailable || "Draft review is not available for this account."
      : !state.online
        ? labels.draftReviewOffline || "Reconnect before reviewing this draft."
        : "";
    return `<form class="inbox-draft-review" data-mobile-inbox-draft-review-form aria-label="${escapeHtml(labels.draftReviewTitle || "Review AI draft")}">
      ${header}
      <p class="inbox-draft-review__body">${escapeHtml(labels.draftReviewBody || "Check and edit the exact message before approval.")}</p>
      <label for="inbox-draft-review-text">${escapeHtml(labels.draftTextLabel || "Draft message")}</label>
      <textarea id="inbox-draft-review-text" data-mobile-inbox-draft-text maxlength="1000" rows="5" ${canEdit && !busy ? "" : "readonly"}>${escapeHtml(inbox.draftReviewText)}</textarea>
      <div class="inbox-draft-review__meta"><span>${inbox.draftReviewText.length}/1000</span><span>${escapeHtml(labels.draftApprovalBoundary || "Approval does not send the message.")}</span></div>
      <label for="inbox-draft-reason">${escapeHtml(labels.draftReasonLabel || "Rejection reason")}</label>
      <select id="inbox-draft-reason" data-mobile-inbox-draft-reason ${busy || !canReject ? "disabled" : ""}>
        <option value="NEEDS_CHANGES" ${inbox.draftReviewReason === "NEEDS_CHANGES" ? "selected" : ""}>${escapeHtml(labels.draftReasonChanges || "Needs changes")}</option>
        <option value="UNSAFE_OR_INCORRECT" ${inbox.draftReviewReason === "UNSAFE_OR_INCORRECT" ? "selected" : ""}>${escapeHtml(labels.draftReasonUnsafe || "Unsafe or incorrect")}</option>
        <option value="WRONG_CONTEXT" ${inbox.draftReviewReason === "WRONG_CONTEXT" ? "selected" : ""}>${escapeHtml(labels.draftReasonContext || "Wrong context")}</option>
        <option value="OPERATOR_REJECTED" ${inbox.draftReviewReason === "OPERATOR_REJECTED" ? "selected" : ""}>${escapeHtml(labels.draftReasonOther || "Operator rejected")}</option>
      </select>
      <p class="inbox-draft-review__status${statusClass}" ${inbox.draftReviewError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(statusMessage || unavailable)}</p>
      <div class="inbox-draft-review__actions">
        <button class="mobile-button mobile-button--secondary" type="submit" data-mobile-inbox-draft-action="reject" ${rejectDisabled ? "disabled" : ""}>${renderIcon("x")}<span>${escapeHtml(busy && inbox.draftReviewAction === "reject" ? labels.draftRejecting || "Rejecting..." : labels.draftReject || "Reject")}</span></button>
        <button class="mobile-button mobile-button--primary" type="submit" data-mobile-inbox-draft-action="approve" ${approveDisabled ? "disabled" : ""}>${renderIcon("shield-check")}<span>${escapeHtml(busy && inbox.draftReviewAction === "approve" ? labels.draftApproving || "Approving..." : labels.draftApprove || "Approve")}</span></button>
      </div>
    </form>`;
  }

  const approved = draft.status === "approved";
  const canSend = approved && capabilities?.send?.enabled === true;
  const sendBoundary = approved
    ? canSend
      ? labels.draftSendBoundary || "Only the approved text will be sent."
      : labels.draftSendUnavailable || "Sending remains blocked until the SMS connection passes every safety check."
    : draft.status === "rejected"
      ? labels.draftRejectedBody || "This draft will not be sent."
      : draft.status === "approved_failed"
        ? labels.draftFailedBody || "Delivery failed. Sending remains blocked until the conversation is refreshed."
        : labels.draftCompleteBody || "This draft is no longer awaiting review.";
  return `<form class="inbox-draft-review" data-mobile-inbox-draft-review-form aria-label="${escapeHtml(labels.draftReviewTitle || "Review AI draft")}">
    ${header}
    <p class="inbox-draft-review__approved-text">${escapeHtml(draft.text || draft.safeSummary || labels.draftTextUnavailable || "Draft text unavailable.")}</p>
    <p class="inbox-draft-review__boundary">${escapeHtml(sendBoundary)}</p>
    ${canSend ? `<label class="inbox-draft-review__confirmation"><input type="checkbox" data-mobile-inbox-draft-confirm ${inbox.draftSendConfirmed ? "checked" : ""} ${busy ? "disabled" : ""}><span>${escapeHtml(labels.draftSendConfirm || "I reviewed this exact message and want to send it.")}</span></label>` : ""}
    <p class="inbox-draft-review__status${statusClass}" ${inbox.draftReviewError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(statusMessage || (!state.online && canSend ? labels.draftReviewOffline || "Reconnect before sending this message." : ""))}</p>
    ${canSend ? `<div class="inbox-draft-review__actions inbox-draft-review__actions--end"><button class="mobile-button mobile-button--primary" type="submit" data-mobile-inbox-draft-action="send" ${busy || !state.online || !inbox.draftSendConfirmed ? "disabled" : ""}>${renderIcon("send-horizontal")}<span>${escapeHtml(busy ? labels.draftSending || "Sending..." : labels.draftSend || "Send approved message")}</span></button></div>` : ""}
  </form>`;
}

function renderInboxContactContext(component: MobileUiComponent, state: MobileAppState) {
  const labels = component.labels || {};
  const inbox = state.inbox;
  const context = inbox.contactContext;
  if (!inbox.selectedConversation) return "";
  if (inbox.contextState === "loading" && !context) {
    return `<section class="inbox-contact-context inbox-contact-context--loading" aria-label="${escapeHtml(labels.contextTitle || "Contact context")}">${loadingRows(labels.contextLoading || "Loading contact context...", 2)}</section>`;
  }

  const unavailable = labels.contextUnavailable || "Unavailable";
  const display = (value: unknown) => readableToken(value, unavailable);
  if (!context) {
    return `<details class="inbox-contact-context">
      <summary><span>${renderIcon("user-round")}<strong>${escapeHtml(labels.contextTitle || "Contact context")}</strong></span><span>${escapeHtml(unavailable)}</span>${renderIcon("chevron-down")}</summary>
      <p class="inbox-contact-context__notice" ${inbox.contextError ? 'role="alert"' : ""}>${escapeHtml(inbox.contextError?.message || labels.contextUnavailableBody || "Contact context is not available for this conversation.")}</p>
    </details>`;
  }

  const memoryState = context.readiness?.memoryProfileAvailable
    ? `${Math.max(0, Number(context.readiness.activeMemoryCount || 0))} ${labels.contextMemories || "memories"}`
    : display(context.readiness?.memoryProjectionState);
  const identifierKinds = (context.endpoint?.identifierKindsAvailable || []).slice(0, 5).map((value) => display(value));
  const tags = (context.contact?.tags || []).slice(0, 8);
  const relationshipSummary = String(context.olivia?.relationshipSummary || "").trim();
  const memorySummary = String(context.olivia?.activeMemorySummary || "").trim();
  const safeSummary = (value: string) => value && value.toLowerCase() !== "unavailable" ? value : "";
  const rows = [
    [labels.contextIdentity || "Identity", display(context.readiness?.identityState)],
    [labels.contextConsent || "Consent", display(context.contact?.consentState)],
    [labels.contextRelationship || "Relationship", display(context.olivia?.relationshipState)],
    [labels.contextMemory || "Memory", memoryState],
    [labels.contextReceive || "Receive", display(context.platform?.receiveCapability)],
    [labels.contextSend || "Send", display(context.platform?.sendCapability)],
    [labels.contextHistory || "History", display(context.history?.completeness || context.history?.origin)],
    [labels.contextIdentifiers || "Identifiers", identifierKinds.length ? identifierKinds.join(", ") : unavailable]
  ];
  return `<details class="inbox-contact-context">
    <summary><span>${renderIcon("user-round")}<strong>${escapeHtml(labels.contextTitle || "Contact context")}</strong></span><span>${escapeHtml(display(context.readiness?.identityState))}</span>${renderIcon("chevron-down")}</summary>
    <div class="inbox-contact-context__body">
      <dl>${rows.map(([term, value]) => `<div><dt>${escapeHtml(term)}</dt><dd>${escapeHtml(value)}</dd></div>`).join("")}</dl>
      ${tags.length ? `<div class="inbox-contact-context__tags" aria-label="${escapeHtml(labels.contextTags || "Labels")}">${tags.map((tag) => `<span>${escapeHtml(tag)}</span>`).join("")}</div>` : ""}
      ${safeSummary(relationshipSummary) ? `<p><strong>${escapeHtml(labels.contextRelationshipSummary || "Relationship context")}</strong>${escapeHtml(relationshipSummary)}</p>` : ""}
      ${safeSummary(memorySummary) ? `<p><strong>${escapeHtml(labels.contextMemorySummary || "Active memory")}</strong>${escapeHtml(memorySummary)}</p>` : ""}
    </div>
  </details>`;
}

const renderConversationDetail: ComponentRenderer = (component, { state }) => {
  const inbox = state.inbox;
  const labels = component.labels || {};
  const backAction = actionFor("back-to-inbox", labels.back || "", "arrow-left");
  const back = textButton(backAction, "back-button");
  if (inbox.detailState === "loading") {
    return `<section class="thread-view">${back}<div class="thread-loading">${loadingRows(labels.loading || state.ui.app.loadingMessage, 5)}</div></section>`;
  }
  if (inbox.detailState === "error" || inbox.detailState === "forbidden") {
    return `<section class="thread-view">${back}${errorState(state, inbox.detailError, "retry-conversation")}</section>`;
  }
  if (!inbox.selectedConversation) {
    return `<section class="thread-placeholder"><span>${renderIcon("message-circle")}</span><h2>${escapeHtml(labels.noSelection)}</h2></section>`;
  }

  const conversation = inbox.selectedConversation;
  const name = contactName(state, conversation);
  const channel = readableToken(conversation.channel, state.ui.app.copy.channelFallback);
  const assignee = conversation.assignee?.label || state.ui.app.copy.unassignedLabel;
  const review = readableToken(conversation.olivia?.approvalMode || conversation.olivia?.state, "Read-only");
  const earlier = actionFor("load-earlier-messages", labels.loadEarlier || "Load earlier messages", "clock");
  const sync = inbox.historySynchronization;
  const coverage = inbox.historyCoverage;
  const historyStatus = coverage?.syncState === "failed" || sync?.retryable ? "History sync failed; saved messages remain available"
    : coverage?.syncState === "syncing" || sync?.inProgress ? "History sync in progress"
    : coverage?.coverage === "unavailable" || sync?.state === "not_linked" ? "Earlier source history unavailable"
    : coverage?.coverage === "complete_within_authorized_scope" ? "History complete within the authorized scope"
    : coverage?.coverage === "partial" || conversation.history?.completeness === "partial" || sync && !sync.historyExhausted ? "Partial conversation history"
    : "History coverage unknown";
  return `<article class="thread-view" aria-labelledby="thread-title">
    ${back}
    <header class="thread-header">
      <span class="avatar avatar--large" aria-hidden="true">${escapeHtml(initials(name, state.ui.app.brandLabel))}</span>
      <div class="thread-header__identity"><h1 id="thread-title">${escapeHtml(name)}</h1><p>${escapeHtml(conversation.contact?.secondaryLabel || channel)}</p></div>
      <div class="thread-header__status"><span>${renderIcon("message-circle")}${escapeHtml(channel)}</span><span>${escapeHtml(assignee)}</span><span>${renderIcon("shield-check")}${escapeHtml(review)}</span></div>
    </header>
    <div class="thread-body" data-scroll-key="inbox-thread-messages" tabindex="0" aria-label="Conversation history">
      <div class="thread-history" role="status"><span>${inbox.messages.length} messages loaded${inbox.messagePage?.hasMore ? " · More saved messages available" : ""}</span><span>${historyStatus}</span></div>
      ${inbox.liveError ? `<p class="thread-history-error" role="alert">${escapeHtml(inbox.liveError.message)}</p>` : inbox.liveReconciliation ? '<p class="thread-history" role="status">Refreshing saved history...</p>' : ""}
      ${inbox.historyError ? `<p class="thread-history-error" role="alert">${escapeHtml(inbox.historyError.message)}</p>` : ""}
      ${inbox.messagePage?.hasMore ? `<button type="button" class="history-button" ${actionAttributes(earlier)} ${inbox.historyState === "loading" || !state.online ? "disabled" : ""}>${renderIcon("clock")}<span>${escapeHtml(inbox.historyState === "loading" ? "Loading earlier messages..." : inbox.historyError ? "Retry earlier messages" : earlier.label)}</span></button>` : ""}
      ${inbox.messages.length ? renderTimeline(state) : `<div class="inline-empty"><span>${renderIcon("message-circle")}</span><p>${escapeHtml(labels.messageEmpty)}</p></div>`}
    </div>
    ${renderInboxContactContext(component, state)}
    ${renderInboxDraftReview(component, state)}
    ${renderInboxComposer(component, state)}
  </article>`;
};

const renderPlaceholder: ComponentRenderer = (component) => `<section class="feature-placeholder feature-placeholder--${escapeHtml(component.tone || "neutral")}">
  <span class="feature-placeholder__icon">${renderIcon(component.icon || "circle-check")}</span>
  ${component.eyebrow ? `<p class="eyebrow">${escapeHtml(component.eyebrow)}</p>` : ""}
  <h1>${escapeHtml(component.title)}</h1>
  <p>${escapeHtml(component.body)}</p>
  ${component.labels?.status ? `<span class="status-pill status-pill--${escapeHtml(component.tone || "neutral")}">${escapeHtml(component.labels.status)}</span>` : ""}
</section>`;

function wctekAgentNames(message: WctekChatMessage, coreLabel: string) {
  const agents = [
    ...(message.orchestration?.selectedAgents || []),
    ...(message.orchestration?.specialistAgents || [])
  ];
  const labels = new Map<string, string>();
  labels.set("ctek", coreLabel);
  for (const agent of agents) {
    const id = String(agent.id || "").trim().toLowerCase();
    const label = id === "ctek" ? coreLabel : String(agent.displayName || "").trim();
    if (id && label) labels.set(id, label);
  }
  return [...labels.values()].slice(0, 4);
}

function renderWctekMessage(component: MobileUiComponent, message: WctekChatMessage) {
  const assistant = message.role === "assistant";
  const labels = component.labels || {};
  const agents = assistant ? wctekAgentNames(message, component.title || "WCTEK AI") : [];
  return `<li class="wctek-message wctek-message--${assistant ? "assistant" : "user"}">
    <article class="wctek-message__bubble">
      <header><strong>${escapeHtml(assistant ? labels.assistantAuthor || "WCTEK" : labels.you || "You")}</strong>${assistant && agents.length > 1 ? `<span>${escapeHtml(labels.specialists || "Agents")}: ${escapeHtml(agents.join(", "))}</span>` : ""}</header>
      <p>${escapeHtml(message.text)}</p>
      ${message.createdAt ? `<time datetime="${escapeHtml(message.createdAt)}">${escapeHtml(formatMessageTime(message.createdAt, ""))}</time>` : ""}
    </article>
  </li>`;
}

function renderWctekSessions(component: MobileUiComponent, state: MobileAppState) {
  const wctek = state.wctek;
  const labels = component.labels || {};
  const retry = actionFor("retry-wctek-history", labels.loadHistory || "Reload recent conversations", "refresh-cw");
  let content = "";
  if (wctek.historyState === "loading" || wctek.historyState === "idle") {
    content = `<p class="wctek-sessions__status" role="status">${escapeHtml(labels.loadingHistory || "Loading recent conversations...")}</p>`;
  } else if (wctek.historyState === "error") {
    content = `<div class="wctek-sessions__status is-error"><span>${escapeHtml(wctek.historyError?.message || labels.empty)}</span>${iconButton(retry)}</div>`;
  } else if (!wctek.sessions.length) {
    content = `<p class="wctek-sessions__status">${escapeHtml(labels.empty || "No previous conversations yet.")}</p>`;
  } else {
    content = `<div class="wctek-session-list">${wctek.sessions.map((session) => {
      const open = actionFor("open-wctek-session", session.title, "message-circle");
      return `<button class="wctek-session" type="button" ${actionAttributes(open, {
        "wctek-session-id": session.sessionId,
        confirmation: labels.discardDraft || "Discard this unsent draft and change conversations?"
      })} ${session.sessionId === wctek.sessionId ? 'aria-current="true"' : ""}>
        <span>${renderIcon("message-circle")}</span>
        <span><strong>${escapeHtml(session.title)}</strong><small>${escapeHtml(formatRowTime(session.updatedAt, `${session.messageCount} messages`))}</small></span>
      </button>`;
    }).join("")}</div>`;
  }
  return `<aside class="wctek-sessions" aria-label="${escapeHtml(labels.recentConversations || "Recent conversations")}">${content}</aside>`;
}

const renderWctekChat: ComponentRenderer = (component, { state }) => {
  if (state.sessionState === "unauthenticated") return renderGuestChat(component, state);
  const wctek = state.wctek;
  const labels = component.labels || {};
  const newSession = actionFor("new-wctek-session", labels.newConversation || "New conversation", "plus");
  const header = `<header class="wctek-header">
    <div><p class="eyebrow">${escapeHtml(component.eyebrow)}</p><h1 id="${escapeHtml(component.id)}-title">${escapeHtml(component.title)}</h1><p>${escapeHtml(component.body)}</p></div>
    <div class="wctek-header__actions"><span class="status-pill status-pill--accent">${escapeHtml(labels.status || "Read-only tools")}</span>${iconButton(newSession, "icon-button", { confirmation: labels.discardDraft || "Discard this unsent draft and start a new conversation?" })}</div>
  </header>`;

  if (wctek.loadState === "loading" || wctek.loadState === "idle") {
    return `<section class="wctek-workspace" aria-labelledby="${escapeHtml(component.id)}-title">${header}${loadingRows(labels.loading || state.ui.app.loadingMessage, 5)}</section>`;
  }
  if (wctek.loadState === "error" || wctek.loadState === "forbidden" || !wctek.threadId || !wctek.sessionId) {
    return `<section class="wctek-workspace" aria-labelledby="${escapeHtml(component.id)}-title">${header}${errorState(state, wctek.error, "retry-wctek", labels.retry)}</section>`;
  }

  const serviceEnabled = state.ui.capabilities.olivia === "available"
    && wctek.service?.status === "online"
    && wctek.service?.capabilities?.chat === true;
  const sending = wctek.sendState === "loading";
  const sendDisabled = sending || !wctek.composer.trim() || !state.online || !serviceEnabled;
  const pending = sending
    ? `<li class="wctek-message wctek-message--user is-pending"><article class="wctek-message__bubble"><header><strong>${escapeHtml(labels.you || "You")}</strong><span>${escapeHtml(labels.sending || "Sending...")}</span></header><p>${escapeHtml(wctek.composer.trim())}</p></article></li>`
    : "";
  const status = !state.online
    ? labels.offlineChat
    : !serviceEnabled
      ? labels.disabled
      : wctek.sendError?.message || (sending ? labels.sending : "");
  const retrySend = wctek.sendError?.retryable && !sending && state.online && serviceEnabled
    ? textButton(actionFor("retry-wctek-send", labels.retrySend || "Retry message", "refresh-cw"), "mobile-button mobile-button--secondary")
    : "";
  const cancel = sending
    ? iconButton(actionFor("cancel-wctek-send", labels.cancel || "Stop request", "x"), "icon-button")
    : "";

  return `<section class="wctek-workspace" aria-labelledby="${escapeHtml(component.id)}-title">
    ${header}
    ${renderWctekSessions(component, state)}
    <div class="wctek-conversation" data-scroll-key="wctek-messages" role="log" tabindex="0" aria-live="polite" aria-label="${escapeHtml(labels.ariaLabel || component.title)}">
      ${wctek.messages.length || pending ? `<ol class="wctek-message-list">${wctek.messages.map((message) => renderWctekMessage(component, message)).join("")}${pending}</ol>` : `<div class="wctek-empty"><span>${renderIcon(component.icon || "sparkles")}</span><h2>${escapeHtml(labels.emptyConversation)}</h2></div>`}
    </div>
    <form class="wctek-composer" data-mobile-wctek-form aria-label="${escapeHtml(labels.composerLabel || "Message WCTEK AI")}">
      <label class="sr-only" for="wctek-composer">${escapeHtml(labels.composerLabel || "Message WCTEK AI")}</label>
      <textarea id="wctek-composer" data-mobile-wctek-composer maxlength="4000" rows="3" placeholder="${escapeHtml(labels.composerPlaceholder)}" ${sending ? "disabled" : ""}>${escapeHtml(wctek.composer)}</textarea>
      <div class="wctek-composer__footer">
        <p class="wctek-composer__status${wctek.sendError || !state.online || !serviceEnabled ? " is-error" : ""}" ${wctek.sendError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(status)}</p>
        <span class="wctek-composer__count">${wctek.composer.length}/4000</span>
        ${retrySend}${cancel}
        <button class="icon-button icon-button--primary" type="submit" aria-label="${escapeHtml(labels.sendMessage || "Send message")}" title="${escapeHtml(labels.sendMessage || "Send message")}" ${sendDisabled ? "disabled" : ""}>${renderIcon(sending ? "refresh-cw" : "send-horizontal")}</button>
      </div>
    </form>
  </section>`;
};

function renderGuestChat(component: MobileUiComponent, state: MobileAppState) {
  const guest = state.guest;
  const disabled = guest.busy || !state.online || !guest.view || guest.view.disabled;
  return `<section class="wctek-workspace" aria-labelledby="guest-wctek-title" data-guest-chat>
    <header class="wctek-header"><div><p class="eyebrow">Free text chat</p><h1 id="guest-wctek-title">WCTEK AI</h1><p>Try a conversation. Sign in for your private account.</p></div></header>
    <p class="wctek-guest-allowance" data-guest-allowance aria-live="polite">${escapeHtml(guest.view?.allowance || "20 completed text replies per UTC day.")}</p>
    <div class="wctek-conversation" data-scroll-key="wctek-messages" role="log" tabindex="0" aria-live="polite" aria-label="Guest conversation">
      ${guest.messages.length ? `<ol class="wctek-message-list">${guest.messages.map(message => renderWctekMessage(component, message as WctekChatMessage)).join("")}</ol>` : `<div class="wctek-empty"><span>${renderIcon("sparkles")}</span><h2>Start a conversation</h2></div>`}
    </div>
    <form class="wctek-composer" data-mobile-wctek-form aria-label="Message WCTEK AI">
      <label class="sr-only" for="wctek-composer">Message WCTEK AI</label>
      <textarea id="wctek-composer" data-mobile-wctek-composer data-guest-composer maxlength="4000" rows="2" enterkeyhint="send" placeholder="Message WCTEK AI…" ${guest.busy ? "disabled" : ""}>${escapeHtml(guest.composer)}</textarea>
      <div class="wctek-composer__footer">
        <p class="wctek-composer__status" role="status">${escapeHtml(!state.online ? "Reconnect to continue" : guest.busy ? "Saving your response…" : guest.error || guest.view?.message || "Connecting")}</p>
        <button type="button" class="mobile-button mobile-button--secondary" ${actionAttributes(actionFor("retry-wctek", "Check response", "refresh-cw"))} ${guest.busy || !state.online || guest.view?.retryDisabled ? "disabled" : ""}>${escapeHtml(guest.view?.retryLabel || "Refresh")}</button>
        <button class="icon-button icon-button--primary" type="submit" aria-label="Send message" ${disabled || !guest.composer.trim() ? "disabled" : ""}>${renderIcon("send-horizontal")}</button>
      </div>
    </form>
  </section>`;
}

const renderProfileSummary: ComponentRenderer = (component, { state }) => {
  const identity = state.session?.identity?.authIdentity || state.session?.authUser || null;
  const signedInAs = identity?.displayName || identity?.email || state.ui.app.brandLabel;
  const active = accountLabel(state.activeAccount, state.ui.app.copy.workspaceLabel);
  return `<section class="profile-summary" aria-labelledby="profile-title">
    ${textButton(profileViewAction("Back to profile", "public"), "back-button")}
    <div class="profile-summary__heading">
      <span class="avatar avatar--profile" aria-hidden="true">${escapeHtml(initials(signedInAs, state.ui.app.brandLabel))}</span>
      <div><h1 id="profile-title">Account settings</h1><p>${escapeHtml(active)}</p></div>
    </div>
    <dl class="profile-facts">
      <div><dt>${escapeHtml(component.labels?.workspace)}</dt><dd>${escapeHtml(active)}</dd></div>
      <div><dt>${escapeHtml(component.labels?.signedInAs)}</dt><dd>${escapeHtml(signedInAs)}</dd></div>
      <div><dt>${escapeHtml(component.labels?.sessionStatus)}</dt><dd><span class="status-pill status-pill--success">${escapeHtml(component.labels?.active)}</span></dd></div>
    </dl>
    <button class="mobile-button mobile-button--secondary" type="button" data-mobile-blocks-open><span>Blocked accounts</span></button>
    <button class="mobile-button mobile-button--secondary" type="button" data-mobile-sms-open hidden>${renderIcon("message-circle")}<span>Phone SMS</span></button>
  </section>`;
};

function renderPublicAccount(state: MobileAppState) {
  const profileState = state.publicProfile;
  const profile = profileState.data;
  const account = profile || state.activeAccount;
  const name = accountLabel(account, "Your account");
  const username = account?.username || "";
  const avatar = profileImageUrl(account?.avatarUrl) || profileImageUrl("/U/assets/default-avatar.svg");
  const url = publicProfileUrl(username);
  const identity = state.sessionState === "ready"
    ? state.session?.identity?.authIdentity || state.session?.authUser
    : null;
  const settings = { ...profileViewAction("Account settings", "hub"), icon: "settings" as const };
  const loading = ["idle", "loading"].includes(profileState.loadState);
  const collections = profile ? (["merch", "inventory", "content"] as const).filter(key => profile.sections[key]).map(key => {
    const label = { content: "Content", merch: "Merch", inventory: "Inventory" }[key];
    return `<section class="account-page__section" aria-labelledby="account-${key}-title">
      <h2 id="account-${key}-title">${label}</h2>
      ${profile[key].length ? `<ul class="account-page__items">${profile[key].map(item => `<li>${escapeHtml(item.title)}</li>`).join("")}</ul>` : `<p class="account-page__empty">No ${key} yet.</p>`}
    </section>`;
  }).join("") : "";
  return `<main class="mobile-main mobile-main--stack mobile-main--account" data-scroll-key="screen-profile-public" aria-label="Your profile">
    <div class="account-page__toolbar"><span>Profile</span><div>
      ${state.sessionState === "ready" ? '<button class="mobile-button mobile-button--secondary" type="button" data-mobile-user-profiles-open>Find an account</button>' : ""}
      ${url ? iconButton(actionFor("open-public-profile", "Open public profile on website", "link")) : ""}
      ${iconButton(settings)}
    </div></div>
    <header class="account-page__identity">
      <div class="account-page__avatar"><span aria-hidden="true">${escapeHtml(initials(name, "EPIC"))}</span>${avatar ? `<img src="${escapeHtml(avatar)}" alt="${escapeHtml(name)} avatar" referrerpolicy="no-referrer" />` : ""}</div>
      <div class="account-page__name"><h1>${escapeHtml(name)}</h1><p class="account-page__handle">@${escapeHtml(username)}</p>${account?.type ? `<p class="account-page__type">${escapeHtml(readableToken(account.type, "Account"))}</p>` : ""}
        ${profile ? `<p class="account-page__bio">${escapeHtml(profile.bio || "No bio yet.")}</p>` : ""}
      </div>
    </header>
    ${profileState.error ? `<div class="account-page__notice" role="alert"><p>${escapeHtml(profileState.error.message)}</p>${textButton(actionFor("retry-public-profile", "Try again", "refresh-cw"), "mobile-button mobile-button--secondary")}</div>` : ""}
    ${loading ? `<div role="status" class="account-page__loading">${profile ? "Refreshing profile..." : loadingRows("Loading your profile...", 3)}</div>` : ""}
    ${state.sessionState === "ready" ? `<section class="account-page__session" aria-label="Your signed-in account">
      <div><h2>Authenticated User</h2><p>${escapeHtml(identity?.displayName || "Signed-in user")}</p>${identity?.email ? `<p class="account-page__session-detail">${escapeHtml(identity.email)}</p>` : ""}</div>
      <div><h2>Active Account</h2><p>${escapeHtml(accountLabel(state.activeAccount, "Your account"))}</p>${state.activeAccount?.type ? `<p class="account-page__session-detail">${escapeHtml(readableToken(state.activeAccount.type, "Account"))}</p>` : ""}</div>
    </section>` : ""}
    ${profile?.sections.companion && url ? `<section class="account-page__section account-page__companion" aria-labelledby="account-companion-title">
      <h2 id="account-companion-title">Companion</h2>
      <div class="account-page__companion-tool"><div><p class="account-page__companion-label">Talk to this account</p><p class="account-page__companion-name">${escapeHtml(name)}</p></div>
        <a href="${escapeHtml(url)}#companion-section" data-mobile-profile-link target="_blank" rel="noopener noreferrer">Open on website ${renderIcon("arrow-right")}</a>
      </div>
    </section>` : ""}
    ${profile?.sections.socialLinks ? `<section class="account-page__section" aria-labelledby="account-social-title"><div class="account-page__section-heading"><h2 id="account-social-title">Socials</h2>${iconButton({ ...profileViewAction("Manage socials", "account"), icon: "user-cog" })}</div><div data-mobile-profile-socials></div></section>` : ""}
    ${collections}
  </main>`;
}

function profileViewAction(label: string, profileView: MobileUiAction["profileView"]): MobileUiAction {
  return { name: "open-profile-view", label, icon: "arrow-left", tab: null, profileView };
}

function renderProfilePanelHeading(component: MobileUiComponent, parent: MobileUiAction["profileView"]) {
  const back = profileViewAction(component.labels?.back || "Back", parent);
  return `<header class="profile-panel-heading">
    ${textButton(back, "back-button")}
    <div>${component.eyebrow ? `<p class="eyebrow">${escapeHtml(component.eyebrow)}</p>` : ""}<h1 id="${escapeHtml(component.id)}-title">${escapeHtml(component.title)}</h1>${component.body ? `<p>${escapeHtml(component.body)}</p>` : ""}</div>
  </header>`;
}

function settingStatus(error: SafeError | null, state: string, labels: Record<string, string>, dirty: boolean, online: boolean) {
  return error?.message
    || (state === "loading" ? labels.saving : "")
    || (!online && dirty ? labels.offlineSave : "")
    || (dirty ? labels.unsaved : "")
    || (state === "ready" ? labels.saved : "");
}

const renderSocialLinksSettings: ComponentRenderer = (component, { state }) => {
  const labels = component.labels || {};
  const socials = state.socialLinks;
  const heading = renderProfilePanelHeading(component, "hub");
  if (socials.loadState === "loading" || socials.loadState === "idle") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${loadingRows(labels.loading || state.ui.app.loadingMessage, 4)}</section>`;
  }
  if (socials.loadState === "error" || socials.loadState === "forbidden") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${errorState(state, socials.error, "retry-social-links", labels.retry)}</section>`;
  }
  const saving = socials.saveState === "loading";
  const status = settingStatus(socials.saveError, socials.saveState, labels, socials.dirty, state.online);
  const addAction = actionFor("add-social-link", labels.add || "Add social link", "plus");
  const reloadAction = actionFor("retry-social-links", labels.reload || "Reload links", "refresh-cw");
  const rows = socials.draft.map((item, index) => {
    const labelId = `social-${index}-label`;
    const platformId = `social-${index}-platform`;
    const urlId = `social-${index}-url`;
    const moveUp = actionFor("move-social-link", labels.moveUp || "Move up", "chevron-up");
    const moveDown = actionFor("move-social-link", labels.moveDown || "Move down", "chevron-down");
    const remove = actionFor("remove-social-link", labels.remove || "Remove", "trash-2");
    return `<fieldset class="social-link-row">
      <legend>${escapeHtml(item.label || `${labels.platform || "Social link"} ${index + 1}`)}</legend>
      <div class="social-link-row__toolbar">
        ${iconButton(moveUp, "icon-button", { "social-index": String(index), direction: "up" }).replace("<button ", `<button ${index === 0 ? "disabled " : ""}`)}
        ${iconButton(moveDown, "icon-button", { "social-index": String(index), direction: "down" }).replace("<button ", `<button ${index === socials.draft.length - 1 ? "disabled " : ""}`)}
        ${iconButton(remove, "icon-button icon-button--danger", { "social-index": String(index) })}
      </div>
      <label class="settings-field" for="${platformId}"><span>${escapeHtml(labels.platform || "Platform")}</span><select id="${platformId}" data-mobile-social-field="platform" data-social-index="${index}">
        ${(component.options || []).map((option) => `<option value="${escapeHtml(option.value)}" ${option.value === item.platform ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
      </select></label>
      <label class="settings-field" for="${labelId}"><span>${escapeHtml(labels.label || "Label")}</span><input id="${labelId}" data-mobile-social-field="label" data-social-index="${index}" type="text" maxlength="80" required value="${escapeHtml(item.label)}"></label>
      <label class="settings-field settings-field--wide" for="${urlId}"><span>${escapeHtml(labels.url || "Profile URL or handle")}</span><input id="${urlId}" data-mobile-social-field="url" data-social-index="${index}" type="text" maxlength="2048" inputmode="url" required value="${escapeHtml(item.url)}"></label>
      <label class="toggle-field settings-field--wide"><input data-mobile-social-field="visible" data-social-index="${index}" type="checkbox" ${item.visible ? "checked" : ""}><span>${escapeHtml(labels.visible || "Show on my profile")}</span></label>
    </fieldset>`;
  }).join("");
  return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">
    ${heading}
    <form class="settings-form" data-mobile-social-links-form aria-label="${escapeHtml(labels.settingsAriaLabel || component.title)}">
      <div class="settings-toolbar">
        <label class="toggle-field"><input data-mobile-social-enabled type="checkbox" ${socials.enabled ? "checked" : ""}><span>${escapeHtml(labels.enabled || "Show social links on my profile")}</span></label>
        ${iconButton(reloadAction)}
      </div>
      ${rows || `<div class="inline-empty"><span>${renderIcon("link")}</span><p>${escapeHtml(labels.empty || "No social links added yet.")}</p></div>`}
      <div class="settings-form__footer">
        ${textButton(addAction, "mobile-button mobile-button--secondary")}
        <p class="settings-form__status${socials.saveError ? " is-error" : ""}" ${socials.saveError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(status)}</p>
        <button class="mobile-button" type="submit" ${saving || !socials.dirty || !state.online ? "disabled" : ""}>${renderIcon(saving ? "refresh-cw" : "save")}<span>${escapeHtml(saving ? labels.saving : labels.save)}</span></button>
      </div>
    </form>
  </section>`;
};

const renderCheckinSettings: ComponentRenderer = (component, { state }) => {
  const labels = component.labels || {};
  const checkins = state.checkins;
  const heading = renderProfilePanelHeading(component, "ai-manager");
  if (checkins.loadState === "loading" || checkins.loadState === "idle") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${loadingRows(labels.loading || state.ui.app.loadingMessage, 4)}</section>`;
  }
  if (checkins.loadState === "error" || checkins.loadState === "forbidden" || !checkins.draft) {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${errorState(state, checkins.error, "retry-checkins", labels.retry)}</section>`;
  }
  const draft = checkins.draft;
  const saving = checkins.saveState === "loading";
  const status = settingStatus(checkins.saveError, checkins.saveState, labels, checkins.dirty, state.online);
  const slots = draft.slots.map((slot) => {
    const slotLabel = slot.id === "morning" ? labels.slotMorning : slot.id === "afternoon" ? labels.slotAfternoon : labels.slotEvening;
    return `<div class="checkin-slot">
      <label class="toggle-field"><input type="checkbox" data-mobile-checkin-field="slot-${escapeHtml(slot.id)}-enabled" ${slot.enabled ? "checked" : ""}><span>${escapeHtml(slotLabel || readableToken(slot.id, "Check-in"))}</span></label>
      <label class="settings-field settings-field--compact"><span class="sr-only">${escapeHtml(`${slotLabel || slot.id} time`)}</span><input type="time" data-mobile-checkin-field="slot-${escapeHtml(slot.id)}-time" value="${escapeHtml(slot.time)}" ${slot.enabled ? "" : "disabled"}></label>
    </div>`;
  }).join("");
  const deviceTimezone = actionFor("use-device-timezone", labels.useDeviceTimezone || "Use device timezone", "clock");
  const reset = actionFor("reset-checkins", labels.reset || "Reset check-ins", "refresh-cw");
  return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">
    ${heading}
    <form class="settings-form" data-mobile-checkin-form aria-label="${escapeHtml(labels.settingsAriaLabel || component.title)}">
      <label class="settings-field"><span>${escapeHtml(labels.frequency || "Frequency")}</span><select data-mobile-checkin-field="frequency">
        <option value="0" ${draft.frequency === 0 ? "selected" : ""}>${escapeHtml(labels.frequencyOff || "Off")}</option>
        <option value="1" ${draft.frequency === 1 ? "selected" : ""}>${escapeHtml(labels.frequencyOne || "Once daily")}</option>
        <option value="2" ${draft.frequency === 2 ? "selected" : ""}>${escapeHtml(labels.frequencyTwo || "Twice daily")}</option>
        <option value="3" ${draft.frequency === 3 ? "selected" : ""}>${escapeHtml(labels.frequencyThree || "Three times daily")}</option>
      </select></label>
      <div class="settings-inline"><label class="settings-field"><span>${escapeHtml(labels.timezone || "Timezone")}</span><input type="text" data-mobile-checkin-field="timezone" maxlength="80" value="${escapeHtml(draft.timezone)}" autocomplete="off"></label>${textButton(deviceTimezone, "mobile-button mobile-button--secondary")}</div>
      <fieldset class="settings-group"><legend>${escapeHtml(labels.status || "Check-in times")}</legend>${slots}</fieldset>
      <fieldset class="settings-group"><legend>${escapeHtml(labels.quietHours || "Quiet hours")}</legend>
        <label class="toggle-field"><input type="checkbox" data-mobile-checkin-field="quiet-enabled" ${draft.quietHours.enabled ? "checked" : ""}><span>${escapeHtml(labels.enabled || "Use quiet hours")}</span></label>
        <div class="settings-inline"><label class="settings-field"><span>${escapeHtml(labels.quietStart || "Starts")}</span><input type="time" data-mobile-checkin-field="quiet-start" value="${escapeHtml(draft.quietHours.start)}"></label><label class="settings-field"><span>${escapeHtml(labels.quietEnd || "Ends")}</span><input type="time" data-mobile-checkin-field="quiet-end" value="${escapeHtml(draft.quietHours.end)}"></label></div>
      </fieldset>
      <label class="settings-field"><span>${escapeHtml(labels.pausedUntil || "Pause through")}</span><input type="date" data-mobile-checkin-field="paused-until" value="${escapeHtml(draft.pausedUntil)}"></label>
      <div class="settings-form__footer">
        ${textButton({ ...reset, label: reset.label }, "mobile-button mobile-button--secondary", { confirmation: labels.confirmReset || "Reset check-ins to their defaults?" })}
        <p class="settings-form__status${checkins.saveError ? " is-error" : ""}" ${checkins.saveError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(status)}</p>
        <button class="mobile-button" type="submit" ${saving || !checkins.dirty || !state.online ? "disabled" : ""}>${renderIcon(saving ? "refresh-cw" : "save")}<span>${escapeHtml(saving ? labels.saving : labels.save)}</span></button>
      </div>
    </form>
  </section>`;
};

const renderMessagingReview: ComponentRenderer = (component, { state }) => {
  const labels = component.labels || {};
  const review = state.reviewDrafts;
  const heading = renderProfilePanelHeading(component, "ai-manager");
  if (review.loadState === "loading" || review.loadState === "idle") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${loadingRows(labels.loading || state.ui.app.loadingMessage, 5)}</section>`;
  }
  if (review.loadState === "error" || review.loadState === "forbidden") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${errorState(state, review.error, "retry-review-drafts", labels.retry)}</section>`;
  }
  if (!review.drafts.length) {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}<div class="empty-state"><span>${renderIcon("file-text")}</span><h2>${escapeHtml(labels.empty || "No drafts need review.")}</h2><p>${escapeHtml(labels.noSend || "Provider sending is not available from this panel.")}</p></div></section>`;
  }
  const selected = review.drafts.find((draft) => draft.draftId === review.selectedDraftId) || null;
  const saving = review.saveState === "loading";
  const closed = selected ? ["rejected", "human_takeover"].includes(String(selected.status)) : true;
  const status = settingStatus(review.saveError, review.saveState, labels, review.dirty, state.online);
  const draftRows = review.drafts.map((draft) => {
    const action = actionFor("select-review-draft", draft.safeDisplayName || "Open draft", "file-text");
    const current = draft.draftId === review.selectedDraftId;
    return `<button class="review-draft-row${current ? " is-selected" : ""}" type="button" ${actionAttributes(action, { "draft-id": draft.draftId })} ${review.dirty && !current ? "disabled" : ""} ${current ? 'aria-current="true"' : ""}>
      <span><strong>${escapeHtml(draft.safeDisplayName || "Conversation draft")}</strong><small>${escapeHtml(readableToken(draft.provider, "Message"))}</small></span>
      <span><small>${escapeHtml(readableToken(draft.status, "Generated"))}</small>${renderIcon("chevron-right")}</span>
    </button>`;
  }).join("");
  const reject = actionFor("reject-review-draft", labels.reject || "Reject draft", "trash-2");
  const takeover = actionFor("human-takeover-review-draft", labels.humanTakeover || "Take over", "user-round");
  return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">
    ${heading}
    <div class="review-layout">
      <nav class="review-draft-list" aria-label="${escapeHtml(labels.ariaLabel || "Drafts to review")}">${draftRows}</nav>
      ${selected ? `<form class="settings-form review-editor" data-mobile-review-form aria-label="${escapeHtml(selected.safeDisplayName || "Draft editor")}">
        ${selected.inboundExcerpt ? `<blockquote><span>${escapeHtml(labels.messageEmpty || "Recent message")}</span>${escapeHtml(selected.inboundExcerpt)}</blockquote>` : ""}
        <label class="settings-field"><span>${escapeHtml(labels.draftText || "Draft")}</span><textarea data-mobile-review-text rows="9" maxlength="4000" ${closed ? "disabled" : ""}>${escapeHtml(review.draftText)}</textarea></label>
        <aside class="safety-note">${renderIcon("shield-check")}<p>${escapeHtml(labels.noSend || "Edits remain drafts. Provider sending is not enabled here.")}</p></aside>
        <div class="settings-form__footer">
          ${textButton(reject, "mobile-button mobile-button--danger", { confirmation: labels.confirmReject || "Reject this draft?" }).replace("<button ", `<button ${closed || review.dirty || saving ? "disabled " : ""}`)}
          ${textButton(takeover, "mobile-button mobile-button--secondary", { confirmation: labels.confirmHumanTakeover || "Mark this conversation for human takeover?" }).replace("<button ", `<button ${closed || review.dirty || saving ? "disabled " : ""}`)}
          <p class="settings-form__status${review.saveError ? " is-error" : ""}" ${review.saveError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(status)}</p>
          <button class="mobile-button" type="submit" ${closed || saving || !review.dirty || !state.online ? "disabled" : ""}>${renderIcon(saving ? "refresh-cw" : "save")}<span>${escapeHtml(saving ? labels.saving : labels.saveDraft || labels.save)}</span></button>
        </div>
      </form>` : ""}
    </div>
  </section>`;
};

function displayDate(value: unknown, fallback = "Not yet") {
  const date = validDate(typeof value === "string" ? value : null);
  return date ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date) : fallback;
}

const renderMemoryHydration: ComponentRenderer = (component, { state }) => {
  const labels = component.labels || {};
  const memory = state.memoryHydration;
  const heading = renderProfilePanelHeading(component, "ai-manager");
  if (memory.loadState === "loading" || memory.loadState === "idle") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${loadingRows(labels.loading || state.ui.app.loadingMessage, 4)}</section>`;
  }
  if (memory.loadState === "error" || memory.loadState === "forbidden" || !memory.data || !memory.draftConsent) {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${errorState(state, memory.error, "retry-memory-hydration", labels.retry)}</section>`;
  }
  const profile = memory.data.profile || {};
  const emotional = memory.data.emotionalMemory || {};
  const conversational = profile.conversational_data || {};
  const saving = memory.saveState === "loading";
  const uploading = memory.uploadState === "loading";
  const consentStatus = settingStatus(memory.saveError, memory.saveState, labels, memory.dirty, state.online);
  const uploadStatus = memory.uploadError?.message || memory.uploadStatus || "";
  const uploadDisabled = uploading || !memory.selectedFile || !memory.uploadConsent || !state.online || memory.draftConsent.conversationalUploadEnabled !== true;
  return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">
    ${heading}
    <dl class="metric-grid">
      <div><dt>${escapeHtml(labels.profileGrounding || "Profile grounding")}</dt><dd>${escapeHtml(readableToken(profile.groundingStatus, "Unavailable"))}</dd></div>
      <div><dt>${escapeHtml(labels.memoryStatus || "Memory status")}</dt><dd>${escapeHtml(readableToken(emotional.status, "Unavailable"))}</dd></div>
      <div><dt>${escapeHtml(labels.messagesScored || "Messages scored")}</dt><dd>${Number(emotional.totalMessagesScored || 0).toLocaleString()}</dd></div>
      <div><dt>${escapeHtml(labels.memoryCells || "Memory cells")}</dt><dd>${Number(emotional.cellCount || 0).toLocaleString()}</dd></div>
      <div><dt>${escapeHtml(labels.uploadComplete || "Imported samples")}</dt><dd>${Number(conversational.sample_count || 0).toLocaleString()}</dd></div>
      <div><dt>${escapeHtml(labels.lastUpdated || "Last updated")}</dt><dd>${escapeHtml(displayDate(emotional.lastUpdatedAt || conversational.last_upload_at))}</dd></div>
    </dl>
    <form class="settings-form settings-form--divided" data-mobile-memory-consent-form aria-label="${escapeHtml(labels.settingsAriaLabel || "Memory consent settings")}">
      <div><h2>${escapeHtml(labels.styleLearning || "Style learning")}</h2><p>${escapeHtml(labels.consentHelp || "Control whether account conversation data may be used to ground this companion's communication style.")}</p></div>
      <label class="toggle-field"><input type="checkbox" data-mobile-memory-consent="styleLearningEnabled" ${memory.draftConsent.styleLearningEnabled ? "checked" : ""}><span>${escapeHtml(labels.styleLearning || "Allow style learning")}</span></label>
      <label class="toggle-field"><input type="checkbox" data-mobile-memory-consent="conversationalUploadEnabled" ${memory.draftConsent.conversationalUploadEnabled ? "checked" : ""}><span>${escapeHtml(labels.conversationUpload || "Allow conversational uploads")}</span></label>
      <div class="settings-form__footer"><p class="settings-form__status${memory.saveError ? " is-error" : ""}" ${memory.saveError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(consentStatus)}</p><button class="mobile-button" type="submit" ${saving || !memory.dirty || !state.online ? "disabled" : ""}>${renderIcon(saving ? "refresh-cw" : "save")}<span>${escapeHtml(saving ? labels.saving : labels.save)}</span></button></div>
    </form>
    <form class="settings-form settings-form--divided" data-mobile-memory-upload-form aria-label="${escapeHtml(labels.upload || "Conversation import")}">
      <div><h2>${escapeHtml(labels.upload || "Import conversation data")}</h2><p>${escapeHtml(labels.desktopUpload || "Mobile supports files up to 40 MB. Use the desktop AI Manager for larger imports.")}</p></div>
      <label class="file-picker"><span>${renderIcon("file-text")}<strong>${escapeHtml(memory.selectedFile?.name || labels.chooseFile || "Choose a file")}</strong></span><input type="file" data-mobile-memory-file accept=".txt,.html,.json,.jsonl,.csv,.zip" ${uploading ? "disabled" : ""}></label>
      <label class="toggle-field"><input type="checkbox" data-mobile-memory-upload-consent ${memory.uploadConsent ? "checked" : ""} ${memory.selectedFile && memory.draftConsent.conversationalUploadEnabled ? "" : "disabled"}><span>${escapeHtml(labels.uploadConsent || "I consent to processing this file for conversation style learning.")}</span></label>
      <div class="settings-form__footer"><p class="settings-form__status${memory.uploadError ? " is-error" : ""}" ${memory.uploadError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(uploadStatus)}</p><button class="mobile-button" type="submit" ${uploadDisabled ? "disabled" : ""}>${renderIcon(uploading ? "refresh-cw" : "plus")}<span>${escapeHtml(uploading ? labels.uploading : labels.upload)}</span></button></div>
    </form>
  </section>`;
};

const renderProviderConnections: ComponentRenderer = (component, { state }) => {
  const labels = component.labels || {};
  const providers = state.providerConnections;
  const heading = renderProfilePanelHeading(component, "ai-manager");
  if (providers.loadState === "loading" || providers.loadState === "idle") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${loadingRows(labels.loading || state.ui.app.loadingMessage, 4)}</section>`;
  }
  if (providers.loadState === "error" || providers.loadState === "forbidden") {
    return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${errorState(state, providers.error, "retry-provider-connections", labels.retry)}</section>`;
  }
  const cards = providers.connections.map((connection) => {
    const tone = ["neutral", "accent", "success", "warning", "danger"].includes(String(connection.state?.tone)) ? connection.state?.tone : "neutral";
    const actions = (connection.actions || []).filter((action) => action.enabled).map((providerAction) => {
      const action = actionFor("execute-provider-action", providerAction.label, providerAction.name === "connect" || providerAction.name === "reconnect" ? "link" : "refresh-cw");
      const active = providers.activeActionKey === `${connection.targetRef}:${providerAction.name}`;
      return textButton(action, "mobile-button mobile-button--secondary", {
        "provider-ref": connection.targetRef,
        "provider-action": providerAction.name,
        ...(providerAction.confirmation?.required ? { confirmation: providerAction.confirmation.message || providerAction.confirmation.title || providerAction.label } : {})
      }).replace("<button ", `<button ${active ? "disabled " : ""}`);
    }).join("");
    return `<article class="provider-card" aria-label="${escapeHtml(`${connection.branding?.label || "Provider"} connection`)}">
      <header><span class="tool-card__icon">${renderIcon("plug")}</span><div><p class="eyebrow">${escapeHtml(connection.branding?.label || "Provider")}</p><h2>${escapeHtml(connection.display?.primary || "Connection")}</h2><p>${escapeHtml(connection.display?.secondary)}</p></div><span class="status-pill status-pill--${escapeHtml(tone)}">${escapeHtml(connection.state?.label || "Unknown")}</span></header>
      ${connection.state?.message ? `<p class="provider-card__message">${escapeHtml(connection.state.message)}</p>` : ""}
      <dl class="provider-indicators">${(connection.indicators || []).map((indicator) => `<div><dt>${escapeHtml(indicator.label)}</dt><dd>${escapeHtml(indicator.display)}</dd></div>`).join("")}</dl>
      ${actions ? `<div class="provider-card__actions">${actions}</div>` : `<p class="panel-note">${escapeHtml(labels.status || "No connection action is needed.")}</p>`}
    </article>`;
  }).join("");
  return `<section class="profile-tool" aria-labelledby="${escapeHtml(component.id)}-title">${heading}${cards || `<div class="empty-state"><span>${renderIcon("plug")}</span><h2>${escapeHtml(labels.empty || "No provider connections are available.")}</h2></div>`}${providers.actionError ? `<p class="settings-form__status is-error" role="alert">${escapeHtml(providers.actionError.message)}</p>` : providers.status ? `<p class="settings-form__status" role="status">${escapeHtml(providers.status)}</p>` : ""}</section>`;
};

function companionFieldValue(state: MobileAppState, key: MobileUiCompanionFieldKey) {
  const draft = state.companion.draft;
  if (!draft) return "";
  const values: Record<MobileUiCompanionFieldKey, string> = {
    "ai-name": draft.aiName,
    "base-personality": draft.basePersonality,
    "communication-style": draft.communicationStyle,
    "hard-refusals": draft.hardRefusals,
    "soft-boundaries": draft.softBoundaries,
    "relationship-boundaries": draft.relationshipBoundaries,
    "ai-reminder-frequency": draft.aiReminderFrequency,
    "redirect-to-real-life": draft.redirectToRealLife
  };
  return values[key];
}

function renderCompanionField(field: MobileUiCompanionField, state: MobileAppState) {
  const id = `companion-${field.key}`;
  const helpId = field.help ? `${id}-help` : "";
  const describedBy = helpId ? ` aria-describedby="${escapeHtml(helpId)}"` : "";
  const value = companionFieldValue(state, field.key);
  const options = field.options || [];
  const control = options.length
    ? `<select id="${escapeHtml(id)}" name="${escapeHtml(field.key)}" data-mobile-profile-field="${escapeHtml(field.key)}"${describedBy}>
        ${options.map((option) => `<option value="${escapeHtml(option.value)}" ${option.value === value ? "selected" : ""}>${escapeHtml(option.label)}</option>`).join("")}
      </select>`
    : ["base-personality", "hard-refusals", "soft-boundaries"].includes(field.key)
      ? `<textarea id="${escapeHtml(id)}" name="${escapeHtml(field.key)}" data-mobile-profile-field="${escapeHtml(field.key)}" rows="${field.key === "base-personality" ? 5 : 4}" maxlength="${field.key === "base-personality" ? 1200 : 900}" placeholder="${escapeHtml(field.placeholder)}"${describedBy}>${escapeHtml(value)}</textarea>`
      : `<input id="${escapeHtml(id)}" name="${escapeHtml(field.key)}" data-mobile-profile-field="${escapeHtml(field.key)}" type="text" maxlength="80" autocomplete="off" value="${escapeHtml(value)}" placeholder="${escapeHtml(field.placeholder)}"${describedBy}>`;
  return `<label class="companion-field companion-field--${escapeHtml(field.key)}" for="${escapeHtml(id)}">
    <span class="companion-field__label">${escapeHtml(field.label)}</span>
    ${control}
    ${field.help ? `<small id="${escapeHtml(helpId)}">${escapeHtml(field.help)}</small>` : ""}
  </label>`;
}

function safePolicyUrl(value: unknown) {
  const url = String(value || "").trim();
  return /^\/legal\/[a-z0-9/_-]*$/i.test(url) ? url : "";
}

function renderPolicyDocuments(state: MobileAppState) {
  const documents = Object.values(state.companion.policy?.policyDocuments || {});
  const links = documents.map((document) => {
    const label = document.label || "Policy";
    const url = document.available === true ? safePolicyUrl(document.url) : "";
    return url
      ? `<li><a href="${escapeHtml(url)}">${escapeHtml(label)}</a></li>`
      : `<li><span>${escapeHtml(label)}</span></li>`;
  });
  return links.length ? `<ul class="companion-policy__documents">${links.join("")}</ul>` : "";
}

const renderCompanionSettings: ComponentRenderer = (component, { state }) => {
  const labels = component.labels || {};
  const companion = state.companion;
  const heading = renderProfilePanelHeading(component, "ai-manager");
  if (companion.loadState === "loading" || companion.loadState === "idle") {
    return `<section class="companion-settings" aria-label="${escapeHtml(labels.settingsAriaLabel || component.title)}">
      ${heading}
      ${loadingRows(labels.loading || state.ui.app.loadingMessage, 4)}
    </section>`;
  }
  if (companion.loadState === "error" || companion.loadState === "forbidden" || !companion.draft) {
    return `<section class="companion-settings" aria-label="${escapeHtml(labels.settingsAriaLabel || component.title)}">
      ${heading}
      ${errorState(state, companion.error, "retry-companion-settings", labels.retry)}
    </section>`;
  }

  const policy = companion.policy;
  const policyAccepted = policy?.accepted === true;
  const documentsAvailable = policy?.documentsAvailable === true;
  const canSavePolicy = policyAccepted || (documentsAvailable && companion.policyAcceptedByUser);
  const saving = companion.saveState === "loading";
  const saveDisabled = saving || !companion.dirty || !state.online || !canSavePolicy;
  const policyMessage = policyAccepted
    ? labels.policyAccepted
    : documentsAvailable
      ? labels.policyRequired
      : labels.policyUnavailable;
  const status = companion.saveError?.message
    || (saving ? labels.saving : "")
    || (!state.online && companion.dirty ? labels.offlineSave : "")
    || (companion.dirty ? labels.unsaved : "")
    || (companion.saveState === "ready" ? labels.saved : "");

  return `<section class="companion-settings" aria-labelledby="${escapeHtml(component.id)}-title">
    ${heading}
    <form class="companion-form" data-mobile-profile-form aria-label="${escapeHtml(labels.settingsAriaLabel || component.title)}">
      <div class="companion-form__fields">${(component.fields || []).map((field) => renderCompanionField(field, state)).join("")}</div>
      <fieldset class="companion-policy" ${policyAccepted ? "disabled" : ""}>
        <legend>${escapeHtml(labels.policyHeading)}</legend>
        <p class="companion-policy__status companion-policy__status--${policyAccepted ? "success" : documentsAvailable ? "neutral" : "warning"}">${escapeHtml(policyMessage)}</p>
        ${renderPolicyDocuments(state)}
        ${policyAccepted ? "" : `<label class="companion-policy__accept"><input type="checkbox" data-mobile-profile-policy ${companion.policyAcceptedByUser ? "checked" : ""} ${documentsAvailable ? "" : "disabled"}><span>${escapeHtml(labels.acceptPolicies)}</span></label>`}
      </fieldset>
      <div class="companion-form__actions">
        <p class="companion-form__status${companion.saveError ? " is-error" : ""}" ${companion.saveError ? 'role="alert"' : 'aria-live="polite"'}>${escapeHtml(status)}</p>
        <button class="mobile-button" type="submit" ${saveDisabled ? "disabled" : ""}>${saving ? renderIcon("refresh-cw") : renderIcon("circle-check")}<span>${escapeHtml(saving ? labels.saving : labels.save)}</span></button>
      </div>
    </form>
  </section>`;
};

const renderSessionActions: ComponentRenderer = (component) => `<section class="session-actions">
  <span class="session-actions__icon">${renderIcon(component.icon || "shield-check")}</span>
  <div class="session-actions__copy">${component.eyebrow ? `<p class="eyebrow">${escapeHtml(component.eyebrow)}</p>` : ""}<h2>${escapeHtml(component.title)}</h2><p>${escapeHtml(component.body)}</p></div>
  <div class="session-actions__buttons">
    ${component.action ? textButton(component.action, "mobile-button") : ""}
    ${component.secondaryAction ? textButton(component.secondaryAction, component.secondaryAction.name === "logout" ? "mobile-button mobile-button--danger" : "mobile-button") : ""}
  </div>
</section>`;

export const COMPONENT_RENDERERS: Record<MobileUiComponentKind, ComponentRenderer> = {
  "app-header": renderAppHeader,
  "bottom-navigation": renderBottomNavigation,
  "connection-status": renderConnectionStatus,
  "account-overview": renderAccountOverview,
  "inbox-preview": renderInboxPreview,
  "summary-grid": renderSummaryGrid,
  "screen-heading": renderScreenHeading,
  "conversation-list": renderConversationList,
  "conversation-detail": renderConversationDetail,
  "wctek-chat": renderWctekChat,
  placeholder: renderPlaceholder,
  "profile-summary": renderProfileSummary,
  "companion-settings": renderCompanionSettings,
  "social-links-settings": renderSocialLinksSettings,
  "checkin-settings": renderCheckinSettings,
  "messaging-review": renderMessagingReview,
  "memory-hydration": renderMemoryHydration,
  "provider-connections": renderProviderConnections,
  "session-actions": renderSessionActions
};

export function renderMobileComponent(component: MobileUiComponent, context: RenderContext) {
  const renderer = COMPONENT_RENDERERS[component.kind];
  return renderer ? renderer(component, context) : "";
}

function renderScreen(state: MobileAppState) {
  if (state.activeTab === "profile" && state.profileView === "public") return renderPublicAccount(state);
  const screen = state.ui.screens[state.activeTab];
  const context = { state, tab: state.activeTab };
  if (screen.layout === "inbox") {
    const primary = screen.components.filter((component) => component.slot !== "detail").map((component) => renderMobileComponent(component, context)).join("");
    const detail = screen.components.filter((component) => component.slot === "detail").map((component) => renderMobileComponent(component, context)).join("");
    const hasThread = Boolean(state.inbox.selectedConversation) || ["loading", "error", "forbidden"].includes(state.inbox.detailState);
    return `<main class="mobile-main mobile-main--inbox${hasThread ? " has-thread" : ""}" aria-label="${escapeHtml(screen.title)}">
      <div class="inbox-pane inbox-pane--list" data-scroll-key="inbox-list">${primary}</div>
      <div class="inbox-pane inbox-pane--detail" data-scroll-key="inbox-thread">${detail}</div>
    </main>`;
  }
  const visibleComponents = state.activeTab === "profile"
    ? screen.components.filter((component) => (component.profileView || "hub") === state.profileView)
    : screen.components;
  return `<main class="mobile-main mobile-main--stack${state.activeTab === "olivia" ? " mobile-main--wctek" : ""}" data-scroll-key="screen-${escapeHtml(state.activeTab)}-${escapeHtml(state.activeTab === "profile" ? state.profileView : "root")}" aria-label="${escapeHtml(screen.title)}">
    ${visibleComponents.map((component) => renderMobileComponent(component, context)).join("")}
  </main>`;
}

function renderUnauthenticated(state: MobileAppState) {
  const copy = state.ui.app.signIn;
  const googleSignInSurface = resolveGoogleSignInSurface();
  const submitting = state.auth.submitState === "loading";
  const disabled = submitting || state.online === false;
  const error = state.auth.error
    ? `<p class="auth-form__error" id="mobile-auth-error" role="alert">${escapeHtml(state.auth.error.message)}</p>`
    : "";
  const describedBy = state.auth.error ? ' aria-describedby="mobile-auth-error"' : "";
  const brand = `<div class="auth-panel__identity">
    <span class="auth-panel__mark" aria-hidden="true"><img src="./icon-192.png" srcset="./icon-192.png 192w, ./icon-512.png 512w" alt="" /></span>
    <strong>${escapeHtml(state.ui.app.brandLabel)}</strong>
  </div>`;

  if (state.auth.stage === "two-factor") {
    return `<main class="mobile-main mobile-main--center">
      <section class="auth-panel" aria-labelledby="mobile-auth-title">
        ${brand}
        <p class="eyebrow">${escapeHtml(copy.twoFactorEyebrow)}</p>
        <h1 id="mobile-auth-title">${escapeHtml(copy.twoFactorTitle)}</h1>
        <p>${escapeHtml(copy.twoFactorBody)}</p>
        <form class="auth-form" data-mobile-auth-two-factor-form novalidate>
          <label class="auth-field" for="mobile-auth-code">
            <span>${escapeHtml(copy.codeLabel)}</span>
            <input id="mobile-auth-code" name="code" type="text" inputmode="text" autocomplete="one-time-code" autocapitalize="none" spellcheck="false" maxlength="80" placeholder="${escapeHtml(copy.codePlaceholder)}" data-mobile-auth-field="code" required${describedBy} />
          </label>
          ${error}
          <button class="mobile-button mobile-button--wide" type="submit" ${disabled ? "disabled" : ""}>
            ${submitting ? '<span class="mobile-spinner mobile-spinner--small" aria-hidden="true"></span>' : renderIcon("shield-check")}
            <span>${escapeHtml(copy.verifyActionLabel)}</span>
          </button>
          <button class="mobile-button mobile-button--secondary mobile-button--wide" type="button" data-mobile-auth-back ${submitting ? "disabled" : ""}>
            ${renderIcon("arrow-left")}<span>${escapeHtml(copy.backActionLabel)}</span>
          </button>
        </form>
      </section>
    </main>`;
  }

  const googleDisabled = disabled || state.auth.configState === "loading";
  const googleAction = googleSignInSurface === "native"
    ? `<button class="mobile-button mobile-button--secondary mobile-button--wide" type="button" data-mobile-system-path="${EPIC_GOOGLE_WEB_SIGN_IN_PATH}" ${googleDisabled ? "disabled" : ""}>${escapeHtml(copy.googleActionLabel)}${renderIcon("arrow-right")}</button>`
    : `<a class="mobile-button mobile-button--secondary mobile-button--wide" href="${EPIC_GOOGLE_WEB_SIGN_IN_PATH}" data-mobile-system-path="${EPIC_GOOGLE_WEB_SIGN_IN_PATH}">${escapeHtml(copy.googleActionLabel)}${renderIcon("arrow-right")}</a>`;
  const google = state.auth.googleAvailable === true && googleSignInSurface !== "unavailable"
    ? `<div class="auth-divider" aria-hidden="true"><span>${escapeHtml(copy.dividerLabel)}</span></div>
      ${googleAction}
      <p class="auth-form__provider-note">${escapeHtml(copy.googleBrowserNote)}</p>`
    : "";
  return `<main class="mobile-main mobile-main--center">
    <section class="auth-panel" aria-labelledby="mobile-auth-title">
      ${brand}
      <p class="eyebrow">${escapeHtml(copy.eyebrow)}</p>
      <h1 id="mobile-auth-title">${escapeHtml(copy.title)}</h1>
      <p>${escapeHtml(copy.body)}</p>
      <form class="auth-form" data-mobile-auth-form novalidate>
        <label class="auth-field" for="mobile-auth-username">
          <span>${escapeHtml(copy.usernameLabel)}</span>
          <input id="mobile-auth-username" name="username" type="text" autocomplete="username" autocapitalize="none" spellcheck="false" maxlength="120" value="${escapeHtml(state.auth.username)}" placeholder="${escapeHtml(copy.usernamePlaceholder)}" data-mobile-auth-field="username" required${describedBy} />
        </label>
        <label class="auth-field" for="mobile-auth-password">
          <span>${escapeHtml(copy.passwordLabel)}</span>
          <span class="auth-password-control">
            <input id="mobile-auth-password" name="password" type="password" autocomplete="current-password" maxlength="256" placeholder="${escapeHtml(copy.passwordPlaceholder)}" data-mobile-auth-field="password" required${describedBy} />
            <button type="button" data-mobile-auth-password-toggle aria-controls="mobile-auth-password" aria-label="${escapeHtml(copy.showPasswordLabel)}" title="${escapeHtml(copy.showPasswordLabel)}" aria-pressed="false" data-show-label="${escapeHtml(copy.showPasswordLabel)}" data-hide-label="${escapeHtml(copy.hidePasswordLabel)}">${renderIcon("eye")}</button>
          </span>
        </label>
        ${error}
        <button class="mobile-button mobile-button--wide" type="submit" ${disabled ? "disabled" : ""}>
          ${submitting ? '<span class="mobile-spinner mobile-spinner--small" aria-hidden="true"></span>' : renderIcon("arrow-right")}
          <span>${escapeHtml(copy.actionLabel)}</span>
        </button>
      </form>
      ${google}
    </section>
  </main>`;
}

function renderLoading(state: MobileAppState) {
  return `<main class="mobile-main mobile-main--center"><section class="loading-panel" role="status"><span class="mobile-spinner" aria-hidden="true"></span><p>${escapeHtml(state.ui.app.loadingMessage)}</p></section></main>`;
}

function renderNoAccount(state: MobileAppState) {
  const copy = state.ui.app.noAccount;
  return `<main class="mobile-main mobile-main--center"><section class="auth-panel">
    <span class="state-panel__icon">${renderIcon("circle-alert")}</span>
    <p class="eyebrow">${escapeHtml(copy.eyebrow)}</p><h1>${escapeHtml(copy.title)}</h1><p>${escapeHtml(copy.body)}</p>
    ${textButton(actionFor("logout", copy.actionLabel, "log-out"), "mobile-button mobile-button--secondary mobile-button--wide")}
  </section></main>`;
}

function renderSessionError(state: MobileAppState) {
  return `<main class="mobile-main mobile-main--center">${errorState(state, {
    status: 0,
    code: "SESSION_LOAD_FAILED",
    message: state.toast || state.ui.app.copy.errorTitle,
    retryable: true,
    requiredAction: null,
    correlationRef: null
  }, "retry-session")}</main>`;
}

export function renderApp(state: MobileAppState) {
  const chromeContext = { state, tab: state.activeTab };
  const header = renderMobileComponent(state.ui.chrome.header, chromeContext);
  const body = state.sessionState === "loading"
    ? renderLoading(state)
    : state.sessionState === "unauthenticated"
      ? MOBILE_GUEST_ENABLED && state.activeTab === "olivia" ? renderScreen(state) : renderUnauthenticated(state)
      : state.sessionState === "forbidden"
        ? renderNoAccount(state)
        : state.sessionState === "error"
          ? renderSessionError(state)
          : renderScreen(state);
  const navigation = state.sessionState === "ready"
    ? renderMobileComponent(state.ui.chrome.navigation, chromeContext)
    : MOBILE_GUEST_ENABLED && state.sessionState === "unauthenticated" ? `<nav class="bottom-navigation" aria-label="Primary">${([
      { name: "navigate", label: "WCTEK AI", icon: "sparkles", tab: "olivia" },
      { name: "navigate", label: "Sign in", icon: "user-round", tab: "profile" }
    ] as MobileUiAction[]).map(action => `<button class="nav-item" type="button" ${actionAttributes(action)} ${state.activeTab === action.tab ? 'aria-current="page"' : ""}>${renderIcon(action.icon || "user-round")}<span>${escapeHtml(action.label)}</span></button>`).join("")}</nav>` : "";
  return `${header}${state.toast ? `<div class="mobile-toast" role="status">${escapeHtml(state.toast)}</div>` : ""}${body}${navigation}`;
}
